import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { Form, message, Button } from "antd";
import dayjs from "dayjs";
import { errorMessage, successMessage } from "../../../utils/notificationUtils";
import { validationPreAuth } from "../../../apis/PreAuthService";
import {
  fetchPreAuthToPortalEchs,
  saveDraftEchs,
  submitApprovalEchs,
  updateDraftEchs,
  getPolyclinic,
  registerValidation,
  updateRegistrationValidationDetails,
} from "../../../apis/SchemeService/ECHS/echsService";
import {
  getAllStarhealthDocumentsData,
  deleteUploadedDocument as deleteUploadDocument,
  deleteReferral,
  updateAllDocumentsData,
  downloadDocument,
} from "../../../apis/patientService";
import {
  getBasicDetails,
  getDeficiencyQuestionData,
  getTimelineDetails,
} from "../../../models/patientSlice";
import {
  fetchStepperData,
  getPortalDataEchs,
} from "../../../models/preAuthSlice";
import { useUserFunctionalities } from "../../../utils/UserFunctionalities";
import { IPreAuthState } from "../../../interfaces/IPreAuth";
import PreAuthEchsForm from "../../../components/patient/PreAuth/PreAuthEchs/PreAuthEchs";
import PreAuthEchs from "../../../components/patient/PreAuth/PreAuthEchs/PreAuthEchs";
import DocumentDeficiencyModal from "../../../components/patient/DocumentDeficiencyModal";
import ConfirmSubmissionModalStage from "../../../components/modals/ConfirmSubmissionModalStage";
import PatientDetailsStageDocument from "../../../components/modals/PatientDetailsStageDocument";
import FilePreviewModal from "../../../components/patient/PreAuth-query/FilePreviewModal";
import { UploadDocument } from "../../../models/EnhancementSlice";
import ManuallyClaimIdPortal from "../../../components/patient/PreAuth/ManuallyClaimIdPortal";
import MultipleFilesPreviewModal from "../../../components/patient/PreAuth-query/MultipleFilesPreviewModal";
import PortalSubmitModal from "../../../components/patient/PreAuth/PortalSubmitModal";
import ManuallyIdModal from "../../../components/patient/PreAuth/ManuallyIdModal";
import PortalSubmissionModal from "../../../components/patient/PreAuth/PortalSubmissionModal";
import ManuallyIdModalForQC from "../../../components/patient/PreAuth/ManuallyIdModal";
import PortalSubmissionModalForQc from "../../../components/patient/PreAuth/PortalSubmissionModal";
import ReferralAcceptance from "../../../components/patient/Referral Acceptance/ReferralAcceptance";
import { errorMessageModal } from "../../../utils/modalMessage";
import ClaimIdValidationModal from "../../../components/patient/Portal Registration/ClaimIdValidationModal";

const GenerateClaimIdModal = PortalSubmissionModal;

interface IDocumentFields {
  documentType: string;
  documentUrl: string;
  documentName: string;
  documentLabelType?: string;
  id?: number;
  deficiencyCheckStatus?: boolean;
}

interface Document {
  id: number;
  type?: string;
  file?: any;
  documentId?: any;
  label?: string;
  labelId?: string | number;
  documentName?: string;
  requestDocument?: boolean;
  responseDocument?: boolean;
}

type ReferralAcceptanceContainerProps = {
  activeKey: any;
  sseData?: any;
  sseUpdateCount?: any;
};

const ReferralAcceptanceContainer: FC<ReferralAcceptanceContainerProps> = ({
  activeKey,
  sseData,
  sseUpdateCount,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const { hasFunctionality } = useUserFunctionalities();

  const [removedDiagnoses, setRemovedDiagnoses] = useState<any[]>([]);

  // Form and UI state
  const [loading, setLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [confirmationState, setConfirmationState] = useState<string>("");
  const [title, setTitle] = useState<string>("");

  // Document related state
  const [documentTypes, setDocumentTypes] = useState<Document[]>([]);
  const [documentUrls, setDocumentUrls] = useState<
    {
      documentType: string;
      documentName: string;
      documentUrl: string;
      id: number;
      referenceType: string | null;
      documentLabelType: any;
      deficiencyCheckStatus: any;
      remarks: string | null;
    }[]
  >([]);
  const [selectedDocument, setSelectedDocument] =
    useState<IDocumentFields | null>(null);
  const [deficiencyModal, setDeficiencyModal] = useState<boolean>(false);
  const [previewDocument, setPreviewDocument] = useState<boolean>(false);
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState<string>("");
  const [documentName, setDocumentName] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<{
    file: File;
    documentType: string;
  } | null>(null);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  });
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [
    documentRequestResponseDocuments,
    setDocumentRequestResponseDocuments,
  ] = useState<any>();
  const [clickedActiveTab, setClickedActiveTab] = useState<any>();
  const [clickedActiveDocumentUrl, setClickedActiveDocumentUrl] =
    useState<any>();

  // Portal submission state
  const [isManuallyEnteredId, setIsManuallyEnteredId] = useState(false);
  const [isManuallyEnteredIdForQc, setIsManuallyEnteredIdForQc] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false); // to check whether submitting api is going or not so that we can show loader acc. to that
  const [isLoadingForQc, setIsLoadingForQc] = useState<boolean>(false);
  const [isSubmittedOnPortal, setIsSubmittedOnPortal] =
    useState<boolean>(false);
  const [isClaimIdPresent, setIsClaimIdPresent] = useState<boolean>(false); // to check whether claim id is present or not for fo
  const [isSubmitOnPortal, setIsSubmitOnPortal] = useState<boolean>(false);
  const [submissionModal, setSubmissionModal] = useState<boolean>(false);
  const [sentForQcModal, setSentForQcModal] = useState<boolean>(false);
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const [isIntimating, setIsIntimating] = useState<boolean>(false);
  const [isApprovedInitiated, setIsApprovedInitiated] =
    useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [fetchingDeficiencyData, setFetchingDeficiencyData] =
    useState<boolean>(false);
  const [portalSubmission, setPortalSubmission] = useState(false);
  const [manuallyClaimIdPortal, setManuallyClaimIdPortal] =
    useState<boolean>(false);
  const [uploadFileList, setUploadFileList] = useState<any[]>([]);
  const [polyclinicList, setPolyclinicList] = useState<any[]>([]);
  const [isGenerateClaimId, setIsGenerateClaimId] = useState<boolean>(false);
  const [isGenerateClaimIdLoading, setIsGenerateClaimIdLoading] =
    useState<boolean>(false);
  const [showVerifyButton, setShowVerifyButton] = useState<boolean>(false);

  const [isErrorState, setIsErrorState] = useState<boolean>(false);
  const lastErrorMessageRef = useRef<string | null>(null);

  useEffect(() => {
    if (isReferralAcceptanceTabActive()) {
      lastErrorMessageRef.current = null;
    }
  }, [activeKey]);

  // Redux state
  // const { auth, patients, preAuth, enhancement } = useSelector(
  //   (state: RootState) => ({
  //     auth: state.auth,
  //     patients: state.patients,
  //     preAuth: state.preAuth as IPreAuthState,
  //     enhancement: state.enhancement,
  //   })
  // );

  const auth = useSelector((state: RootState) => state.auth);
  const patients = useSelector((state: RootState) => state.patients);
  const preAuth = useSelector(
    (state: RootState) => state.preAuth as IPreAuthState,
  );
  const enhancement = useSelector((state: RootState) => state.enhancement);
  const { cases } = useSelector((state: RootState) => state);

  const [selectedFiles, setSelectedFiles] = useState<
    { file: File; documentType: string }[]
  >([]);
  const [previewFiles, setPreviewFiles] = useState<
    {
      url: string;
      name: string;
      showUpload: boolean;
      documentType: string;
      isMultiple: boolean;
    }[]
  >([]);
  const [registrationValidationStatus, setRegistrationValidationStatus] =
    useState<any>(null);
  const [isRegistraionValidatedModalOpen, setIsRegistraionValidatedModalOpen] =
    useState(false);
  const [validationRes, setValidationRes] = useState<any>(null);
  const [isValidationLoading, setIsValidationLoading] =
    useState<boolean>(false);

  useEffect(() => {
    if (!previewModalVisible) {
      setSelectedFiles([]);
    }
  }, [previewModalVisible]);

  const currentActiveStage = preAuth?.stepperData?.visitStages?.find(
    (stage: any) =>
      stage?.visitStageId ===
      (typeof activeKey === "string"
        ? parseInt(activeKey)
        : parseInt(activeKey?.[0])),
  );

  useEffect(() => {
    const fetchPolyclinicList = async () => {
      const hospitalId =
        patients?.fetchBasicDetails?.casesResponse?.healthCareId;
      const schemeId =
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;

      if (hospitalId && schemeId) {
        try {
          const response = await getPolyclinic(hospitalId, schemeId);
          setPolyclinicList(response);
        } catch (error) {
          console.error("Failed to fetch polyclinic list:", error);
          message.error("Failed to fetch polyclinic list");
        }
      }
    };

    fetchPolyclinicList();
  }, [
    patients?.fetchBasicDetails?.casesResponse?.healthCareId,
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
  ]);

  const isReferralAcceptanceTabActive = () => {
    if (!activeKey || activeKey.length === 0) return false;

    const currentStageId =
      typeof activeKey === "string"
        ? parseInt(activeKey)
        : parseInt(activeKey?.[0]);

    const currentStage = preAuth?.stepperData?.visitStages?.find(
      (stage: any) => stage?.visitStageId === currentStageId,
    );

    setIsErrorState(currentStage?.state?.toLowerCase() === "error");

    return (
      currentStage?.visitStage?.toUpperCase() ===
      "ClaimId Generation"?.toUpperCase()
    );
  };

  useEffect(() => {
    if (
      isErrorState &&
      preAuth?.portalData?.claimIdGenerationErrorMessage &&
      preAuth?.portalData?.claimIdGenerationErrorMessage !==
        lastErrorMessageRef.current &&
      isReferralAcceptanceTabActive()
    ) {
      errorMessageModal(preAuth?.portalData?.claimIdGenerationErrorMessage);
      lastErrorMessageRef.current =
        preAuth?.portalData?.claimIdGenerationErrorMessage;
    }
  }, [preAuth?.portalData, isErrorState, activeKey]);

  const handleRegistrationValidation = async () => {
    try {
      const payload = {
        registrationId:
          preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsReferralResponse
            ?.claimId ||
          preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsEmergencyResponse
            ?.claimId,
        caseId: patients?.fetchBasicDetails?.casesResponse?.id,
        visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        healthcareId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
      };
      setIsValidationLoading(true);
      if (registrationValidationStatus?.toUpperCase() === "VALIDATED") {
        setIsValidationLoading(true);

        try {
          // const validationRes = await getRegisterValidation(
          //   preAuth?.portalData?.schemeRegistrationId,
          //   patients?.fetchBasicDetails?.visitInfoResponse?.id
          // );

          const validationRes = await registerValidation(payload);

          setValidationRes(validationRes);

          const status =
            validationRes?.status ||
            validationRes?.data?.status ||
            validationRes?.validationStatus;

          if (
            status?.toLowerCase() === "validated" ||
            status?.toLowerCase() === "completed" ||
            status?.toLowerCase() === "failed"
          ) {
            successMessage("Registration validation completed!");
            if (
              status?.toLowerCase() === "validated" &&
              validationRes?.discrepancyStatus
            ) {
              setRegistrationValidationStatus("Discrepancy");
            } else setRegistrationValidationStatus(status);

            // if (
            //   status?.toLowerCase() === "validated" &&
            //   validationRes?.discrepancyStatus
            // ) {
            setIsRegistraionValidatedModalOpen(true);
            // }

            setIsValidationLoading(false);
            return;
          }

          // mirrors original timeout behavior
          errorMessage("Request timeout.");
          setIsValidationLoading(false);
          setRegistrationValidationStatus("Failed");
        } catch (err) {
          console.error("Error while polling validation:", err);
          setIsValidationLoading(false);
          errorMessage("An error occurred while checking validation.");
        }

        return;
      }

      const res = await registerValidation(payload);

      setValidationRes(res);

      const status = res?.status || res?.data?.status || res?.validationStatus;

      if (
        status?.toLowerCase() === "validated" ||
        status?.toLowerCase() === "completed" ||
        status?.toLowerCase() === "failed"
      ) {
        successMessage("Registration validation completed!");
        // clearInterval(intervalId);
        if (status?.toLowerCase() === "validated" && res?.discrepancyStatus) {
          setRegistrationValidationStatus("Discrepancy");
        } else setRegistrationValidationStatus(status);

        // if (status?.toLowerCase() === "validated" && res?.discrepancyStatus) {
        setIsRegistraionValidatedModalOpen(true);
        // }
        setIsValidationLoading(false);
        return;
      }
    } catch (err) {
      setIsValidationLoading(false);
      errorMessage("Please, Try again after sometime");
    }
  };

  const visitType = React.useMemo(
    () =>
      patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase(),
    [patients?.fetchBasicDetails?.visitInfoResponse?.visitType],
  );
  const [referralPresent, setReferralPresent] = useState<boolean>();

  // const ageAboveSeventyWithoutReferral =
  //   patients?.fetchBasicDetails?.patientResponse?.age &&
  //   patients?.fetchBasicDetails?.patientResponse?.age >= 70 &&
  //   !patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
  //     ? true
  //     : false;
  // const ageBelowSeventyWithoutReferral =
  //   patients?.fetchBasicDetails?.patientResponse?.age &&
  //   patients?.fetchBasicDetails?.patientResponse?.age < 70 &&
  //   !patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
  //     ? true
  //     : false;

  const ageAboveSeventyWithoutReferral = useMemo(() => {
    return patients?.fetchBasicDetails?.patientResponse?.age &&
      patients?.fetchBasicDetails?.patientResponse?.age >= 70 &&
      !patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
      ? true
      : false;
  }, [
    patients?.fetchBasicDetails?.patientResponse?.age,
    patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse,
  ]);

  const ageBelowSeventyWithoutReferral = useMemo(() => {
    return patients?.fetchBasicDetails?.patientResponse?.age &&
      patients?.fetchBasicDetails?.patientResponse?.age < 70 &&
      !patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
      ? true
      : false;
  }, [
    patients?.fetchBasicDetails?.patientResponse?.age,
    patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse,
  ]);

  const clinicalConditions = [
    {
      key: "bp",
      label: "BLOOD PRESSURE",
      valuePrefix: "1",
    },
    {
      key: "cvs",
      label: "CARDIOVASCULAR SYSTEM",
      valuePrefix: "2",
    },
    {
      key: "rs",
      label: "RESPIRATORY SYSTEM",
      valuePrefix: "3",
    },
    {
      key: "abdomen",
      label: "ABDOMEN",
      valuePrefix: "4",
    },
    {
      key: "cns",
      label: "CENTRAL NERVOUS SYSTEM",
      valuePrefix: "5",
    },
    {
      key: "covid",
      label: "COVID",
      valuePrefix: "6",
    },
    {
      key: "clinicalOther",
      label: "OTHER",
      valuePrefix: "99",
    },
  ];

  const treatmentConditions = [
    {
      key: "investigations",
      label: "INVESTIGATIONS",
      valuePrefix: "1",
    },
    {
      key: "intensiveCare",
      label: "INTENSIVE CARE",
      valuePrefix: "2",
    },
    {
      key: "medicalManagement",
      label: "MEDICAL MANAGEMENT",
      valuePrefix: "3",
    },
    {
      key: "surgicalManagement",
      label: "SURGICAL MANAGEMENT",
      valuePrefix: "4",
    },
    {
      key: "minorDescription",
      label: "PROCEDURE MINOR DESCRIPTION",
      valuePrefix: "5",
    },
    {
      key: "majorDescription",
      label: "PROCEDURE MAJOR DESCRIPTION",
      valuePrefix: "6",
    },
    {
      key: "proposedOther",
      label: "PROPOSED OTHER",
      valuePrefix: "99",
    },
  ];

  const medicalConditions = [
    {
      key: "hypertension",
      label: "HYPERTENSION",
      valuePrefix: "1",
    },
    {
      key: "diabetic",
      label: "DIABETIC",
      valuePrefix: "2",
    },
    {
      key: "heart",
      label: "HEART",
      valuePrefix: "3",
    },
    {
      key: "copd",
      label: "CHRONIC OBSTRUCTIVE PULMONARY DISEASE (COPD)",
      valuePrefix: "4",
    },
    {
      key: "asthma",
      label: "BRONCHIAL ASTHMA",
      valuePrefix: "5",
    },
    {
      key: "std",
      label: "SEXUALLY TRANSMITTED DISEASE (STD)",
      valuePrefix: "6",
    },
    {
      key: "alcohol",
      label: "ALCOHOL/DRUGS INTOXICATION",
      valuePrefix: "7",
    },
    {
      key: "other",
      label: "OTHER DISEASE",
      valuePrefix: "99",
    },
  ];

  useEffect(() => {
    setReferralPresent(
      (patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
        ?.referralNo &&
        patients?.fetchBasicDetails.visitInfoResponse?.visitType?.toUpperCase() !==
          "EMERGENCY") ||
        ((ageAboveSeventyWithoutReferral || ageBelowSeventyWithoutReferral) &&
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() !==
            "EMERGENCY")
        ? true
        : false,
    );
  }, [patients?.fetchBasicDetails]);

  useEffect(() => {
    const documentList = [
      ...documentTypes
        ?.filter(
          (item: any) => item.documentList && item.documentList.length > 0,
        )
        ?.flatMap((item: any, index: number) =>
          item.documentList!?.map((doc: any, docIndex: any) => ({
            key: `${index + 1}`,
            label: item.label, // Document Type
            documentUrl: doc.documentUrl, // URL for download
            id: doc.id,
            documentType: doc.documentType,
            documentName: doc.documentName,
            deficiencyCheckStatus:
              doc.deficiencyCheckStatus === true
                ? true
                : doc.deficiencyCheckStatus === false
                  ? false
                  : null,
            deficiency:
              doc.deficiencyCheckStatus === true
                ? true
                : doc.deficiencyCheckStatus === false
                  ? false
                  : null,
            remarks: doc.remarks || null,
            documentLabelType: doc.documentLabelType,
            actions: item,
          })),
        ),
    ];

    setDocumentUrls(documentList);
  }, [documentTypes]);

  useEffect(() => {
    const requestResponseDocuments = {
      requestedDocuments: [] as Document[],
      responseDocuments: [] as Document[],
    };

    documentTypes?.forEach((document) => {
      if (document?.requestDocument) {
        requestResponseDocuments.requestedDocuments.push(document);
      } else if (document?.responseDocument) {
        requestResponseDocuments.responseDocuments.push(document);
      }
    });

    setDocumentRequestResponseDocuments(requestResponseDocuments);
  }, [documentTypes]);

  // Document handling functions
  // const handleFileChange = (documentType: string) => (e: any) => {
  //   const maxSize = 15 * 1024 * 1024;

  //   if (Array.isArray(e)) {
  //     return e;
  //   }
  //   if (e?.file) {
  //     setSelectedFile({ file: e.file, documentType: documentType });
  //     setPreviewFile({
  //       url: URL.createObjectURL(e.file),
  //       name: e.file.name,
  //       showUpload: true,
  //       documentType: documentType,
  //     });
  //     setPreviewModalVisible(true);
  //   }
  //   return e?.target?.files;
  // };

  const handleFileChange =
    (documentType: string, isMultiple: boolean) => (e: any) => {
      if (isMultiple) {
        const files = e?.fileList || [];

        if (e?.fileList[0]?.type !== "application/pdf") {
          errorMessageModal("File Must be PDF only");
          return;
        }

        const maxSize = 1 * 1024 * 1024;
        if (e?.fileList[0]?.size > maxSize && patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() === "ECHS" && patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() === "OP") {
          errorMessage("File Limit must be less than or equal to 1 MB");
          return;
        }

        setUploadFileList(files);
        if (files.length === 0) return;

        const selected = files?.map((f: any) => ({
          file: f.originFileObj,
          documentType,
        }));

        const previews = files?.map((f: any) => ({
          url: URL.createObjectURL(f.originFileObj),
          name: f.name,
          showUpload: true,
          documentType,
          isMultiple: isMultiple,
        }));

        setSelectedFiles(selected);
        setPreviewFiles(previews);
        setPreviewModalVisible(true);
      } else {
        if (Array.isArray(e)) {
          return e;
        }
        if (e.file?.type !== "application/pdf") {
          errorMessageModal("File Must be PDF only");
          return;
        }

        const maxSize = 1 * 1024 * 1024;
        if (e?.file?.size > maxSize && patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() === "ECHS" && patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() === "OP") {
          errorMessage("File Limit must be less than or equal to 1 MB");
          return;
        }

        if (e?.file) {
          setSelectedFile({ file: e.file, documentType: documentType });
          setPreviewFile({
            url: URL.createObjectURL(e.file),
            name: e.file.name,
            showUpload: true,
            documentType: documentType,
          });
          setPreviewModalVisible(true);
        }
        return e?.target?.files;
      }
    };

  const handleDeleteList = async (
    documentType: string,
    documentUrl: string,
    i: number,
  ) => {
    const combinedUrls = [...documentUrls];
    const deleteDocument = combinedUrls.find(
      (doc) => doc.documentUrl === documentUrl,
    );
    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };

    const newUpdatedDocumentList = combinedUrls.filter(
      (doc) => doc.documentUrl !== documentUrl,
    );
    setDocumentUrls(newUpdatedDocumentList);

    if (deleteDocument?.documentUrl) {
      try {
        await deleteUploadDocument(deleteForm);
        if (
          documentType?.toUpperCase() === "REFERRAL LETTER" &&
          patients?.fetchBasicDetails?.referralInfoResponse
        ) {
          const deletedForm = {
            echsReferralRequest: patients?.fetchBasicDetails
              ?.referralInfoResponse?.echsReferralResponse
              ? {
                  status: false,
                }
              : null,
            cghsReferralRequest: null,
          };
          await deleteReferral(
            patients?.fetchBasicDetails?.casesResponse?.id,
            patients?.fetchBasicDetails?.referralInfoResponse
              ?.echsReferralResponse?.id,
            deletedForm,
          );
          if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
            await dispatch(
              getBasicDetails({
                id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
              }),
            );
          }
        }
      } catch (error) {
        errorMessageModal("Failed to delete document");
      }
    }
  };

  const handleDownloadDocument = async (fileName: string, url: string) => {
    try {
      await downloadDocument(fileName, url);
      successMessage("document downloaded successfully");
    } catch (err) {
      errorMessageModal("Please, Try again later");
    }
  };

  const handleOpen = (docType: any, documentUrl: any) => {
    setPreviewDocument(true);
    setClickedActiveTab(docType);
    setClickedActiveDocumentUrl(documentUrl);
  };

  const handleCancelPreview = () => {
    setPreviewDocument(false);
    setPreviewDocumentUrl("");
    setDocumentName("");
  };

  const fetchDeficiencyData = async (
    selectedDocument: any,
    selectedDocumentId: any,
  ) => {
    try {
      if (selectedDocumentId) {
        // dispatch(clearDeficiencyCheck());
        // const firstRes = await dispatch(
        //   getDeficiencyQuestions(selectedDocumentId)
        // );
        setFetchingDeficiencyData(true);
        const secondRes = await dispatch(
          getDeficiencyQuestionData(selectedDocumentId),
        );
        setFetchingDeficiencyData(false);
      }
    } catch (err) {
      errorMessage("Please, Try again after sometime");
      setSelectedDocument(null);
      setFetchingDeficiencyData(false);
    }
  };

  const handleDeficiencyModalOpen = async (
    documentUrl: string,
    documentId: number,
    document: any,
  ) => {
    await fetchDeficiencyData(document, document?.id);
    setPreviewDocumentUrl(documentUrl);
    // setDeficiencyModal(true);
    setSelectedDocument(document);
  };

  const closeDeficiencyModal = () => {
    setDeficiencyModal(false);
    setDocumentName("");
  };

  // Format document data for display
  const formattedDataSource = [
    ...documentUrls?.map((doc, index) => ({
      key: `${index + 1}`,
      label: doc.documentLabelType || "New Upload",
      documentUrl: doc.documentUrl,
      documentType: doc.documentType,
      id: doc.id,
      document: (
        <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer">
          {doc.documentName}
        </a>
      ),
      deficiency: doc.deficiencyCheckStatus,
      documentName: doc.documentName,
      deficiencyCheckStatus: doc.deficiencyCheckStatus,
      documentLabelType: doc.documentLabelType,
      remarks: doc.remarks || "N/A",
      actions: {},
    })),
  ];

  // Fetch documents
  const fetchDocuments = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId &&
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      try {
        setLoading(true);
        const AllDocumentList = await getAllStarhealthDocumentsData(
          patients?.fetchBasicDetails?.casesResponse?.id,
          patients?.fetchBasicDetails?.visitInfoResponse?.id,
          patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        );
        setDocumentTypes(AllDocumentList?.["ClaimId Generation"]);
      } catch (err) {
        // errorMessage("Please, Try again after sometime");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (activeKey && activeKey?.length > 0 && isReferralAcceptanceTabActive())
      fetchDocuments();
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
    activeKey,
  ]);

  useEffect(() => {
    if (!isReferralAcceptanceTabActive()) return;

    // fetchStepperForStatus();
    // getTimelineDetail();

    const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
    const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
    const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
    const casePreAuthResponse =
      referralResponse?.casePreAuthResponse ||
      emergencyResponse?.casePreAuthResponse;
    setPortalSubmission(
      casePreAuthResponse?.submitted ||
        preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsReferralResponse
          ?.claimId ||
        preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsEmergencyResponse
          ?.claimId,
    );
    if (
      preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsReferralResponse
        ?.claimId ||
      preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsEmergencyResponse
        ?.claimId
    ) {
      setShowVerifyButton(true);
    }
    setIsApproved(casePreAuthResponse?.approved);
  }, [
    preAuth?.portalData,
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    activeKey,
  ]);

  useEffect(() => {
      setRegistrationValidationStatus(
        preAuth?.portalData?.portalRegistrationStatus,
      );
    }, [preAuth?.portalData]);

  useEffect(() => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id &&
      isReferralAcceptanceTabActive()
    ) {
      dispatch(
        getPortalDataEchs({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        }),
      );
    }
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    activeKey,
  ]);

  const fetchDataForSse = async () => {
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      // dispatch(
      //   getBasicDetails({
      //     id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
      //   })
      // );

      dispatch(
        getPortalDataEchs({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id || 0,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id || 0,
        }),
      );
    }
    fetchStepperForStatus();
    await getTimelineDetail();
    fetchDocuments();
  };

  useEffect(() => {
    if (sseData) {
      fetchDataForSse();
    }
  }, [sseData, sseUpdateCount]);

  const updateOrAddDocument = (
    uploadedDocumentUrl: any,
    documentRes: IDocumentFields,
  ) => {
    const updatedDocuments = [...uploadedDocumentUrl];

    updatedDocuments.push({
      documentType: documentRes?.documentType,
      documentName: documentRes?.documentName,
      documentUrl: documentRes?.documentUrl,
      documentLabelType: documentRes?.documentLabelType,
      id: documentRes?.id,
      deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
    });

    // Return the updated array (ensures it's a flat array)
    return updatedDocuments;
  };

  const parseMedicalHistoryData = (medicalHistory: any) => {
    if (!medicalHistory) return {};

    const parsedData: any = {};
    Object.entries(medicalHistory)?.forEach(([key, value]) => {
      if (typeof value === "string") {
        const [prefix, status, fieldValue] = (value as string).split(":");
        parsedData[key] = `${prefix}:${status}`;
        if (status === "Y" && fieldValue !== "null") {
          parsedData[`${key}Value`] = fieldValue;
        } else {
          parsedData[`${key}Value`] = "";
        }
      }
    });
    return parsedData;
  };

  const parseFormSectionData = (sectionData: any) => {
    if (!sectionData) return {};

    const parsedData: any = {};
    Object.entries(sectionData).forEach(([key, value]) => {
      if (typeof value === "string") {
        const [prefix, status, fieldValue] = (value as string).split(":");
        parsedData[key] = `${prefix}:${status}`;
        if (status === "Y" && fieldValue !== "null") {
          parsedData[`${key}Value`] = fieldValue;
        } else {
          parsedData[`${key}Value`] = "";
        }
      }
    });
    return parsedData;
  };

  const roomType = Form.useWatch("roomType", form);
  const opdDate = Form.useWatch("opdDate", form);

  useEffect(() => {
    form.setFieldValue("roomNumber", roomType);
  }, [roomType]);

  useEffect(() => {
    if (
      patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
        "IP" ||
      patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
        "EMERGENCY"
    ) {
      const dod =
        opdDate && dayjs(opdDate, "DD-MM-YYYY HH:mm:ss", true).isValid()
          ? dayjs(opdDate, "DD-MM-YYYY HH:mm:ss").add(2, "day")
          : null;

      form.setFieldValue("expectedDischargeDate", dod);
    } else {
      form.setFieldValue("expectedDischargeDate", null);
    }
  }, [opdDate, patients?.fetchBasicDetails?.visitInfoResponse?.visitType]);

  useEffect(() => {
    if (
      !preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsEmergencyResponse
        ?.id &&
      !preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsReferralResponse?.id
    ) {
      // For initial submission, determine type based on visit type and referral presence
      const visitTypeUpper =
        patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
      const isEmergencyCase =
        visitTypeUpper === "EMERGENCY" || !referralPresent;

      form.setFieldsValue({
        admissionType: visitTypeUpper,
        beneficiaryId: patients?.fetchBasicDetails?.casesResponse?.memberId,
        patientOpdIpdNumber:
          patients?.fetchBasicDetails?.visitInfoResponse?.opNumber,
        approxTreatmentCost:
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
            "IP" ||
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
            "EMERGENCY"
            ? 50000
            : null,
        // roomNumber: patients?.fetchBasicDetails?.visitInfoResponse?.roomNumber,
        roomNumber: patients?.fetchBasicDetails?.patientResponse?.cardCategory,
        roomType: patients?.fetchBasicDetails?.patientResponse?.cardCategory,
        remarks: "Intimation Submitted",
        // : patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
        //       "IP" ||
        //     patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
        //       "EMERGENCY"
        //   ? "IPD"
        //   : "OPD",
        type: isEmergencyCase ? "emergency" : "referral",
        opdDate:
          patients?.fetchBasicDetails?.visitInfoResponse?.doa &&
          dayjs(
            patients?.fetchBasicDetails?.visitInfoResponse?.doa,
            "DD-MM-YYYY HH:mm:ss",
            true,
          ).isValid()
            ? dayjs(
                patients?.fetchBasicDetails?.visitInfoResponse?.doa,
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
        // expectedDischargeDate:
        //   patients?.fetchBasicDetails?.visitInfoResponse?.dod &&
        //   dayjs(
        //     patients?.fetchBasicDetails?.visitInfoResponse?.dod,
        //     "DD-MM-YYYY HH:mm:ss",
        //     true
        //   ).isValid()
        //     ? dayjs(
        //         patients?.fetchBasicDetails?.visitInfoResponse?.dod,
        //         "DD-MM-YYYY HH:mm:ss"
        //       )
        //     : null,
        polyClinic:
          patients?.fetchBasicDetails?.referralInfoResponse
            ?.echsReferralResponse?.polyClinic,
        referralId:
          patients?.fetchBasicDetails?.referralInfoResponse
            ?.echsReferralResponse?.referralNo,
        serviceNumber:
          patients?.fetchBasicDetails?.referralInfoResponse
            ?.echsReferralResponse?.serviceNumber,
        cardId:
          patients?.fetchBasicDetails?.referralInfoResponse
            ?.echsReferralResponse?.cardNumber,
        followUpClaimId: patients?.fetchBasicDetails?.referralInfoResponse?.id,
        opNumber: patients?.fetchBasicDetails?.visitInfoResponse?.opNumber,
        isRta: "false",
        admissionAilment:
          patients?.fetchBasicDetails?.referralInfoResponse
            ?.echsReferralResponse?.clinicalNotes,
        admissionDate:
          patients?.fetchBasicDetails?.visitInfoResponse?.doa &&
          dayjs(
            patients?.fetchBasicDetails?.visitInfoResponse?.doa,
            "DD-MM-YYYY HH:mm:ss",
            true,
          ).isValid()
            ? dayjs(
                patients?.fetchBasicDetails?.visitInfoResponse?.doa,
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
        patientDetails: {
          aadharStatus: "Patient has an Aadhaar Number",
        },
      });
    } else {
      // For existing data, check which type of response exists
      const referralResponse =
        preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsReferralResponse;
      const emergencyResponse =
        preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsEmergencyResponse;

      if (referralResponse?.id) {
        // Handle referral response data
        const formValues = {
          ...referralResponse,
          type: "referral",
          beneficiaryId: referralResponse?.casePreAuthResponse?.beneficiaryId,
          patientOpdIpdNumber:
            referralResponse?.casePreAuthResponse?.patientOpdIpdNumber,
          opdDate:
            referralResponse?.admissionData?.opdDate &&
            dayjs(
              referralResponse?.admissionData?.opdDate,
              "DD-MM-YYYY HH:mm:ss",
              true,
            ).isValid()
              ? dayjs(
                  referralResponse?.admissionData?.opdDate,
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
          admissionDate:
            referralResponse?.admissionDate &&
            dayjs(
              referralResponse?.admissionDate,
              "DD-MM-YYYY HH:mm:ss",
              true,
            ).isValid()
              ? dayjs(referralResponse?.admissionDate, "DD-MM-YYYY HH:mm:ss")
              : null,
          expectedDischargeDate:
            referralResponse?.admissionData?.expectedDischargeDate &&
            dayjs(
              referralResponse?.admissionData?.expectedDischargeDate,
              "DD-MM-YYYY HH:mm:ss",
              true,
            ).isValid()
              ? dayjs(
                  referralResponse?.admissionData?.expectedDischargeDate,
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
          roomNumber: referralResponse?.admissionData?.roomNumber,
          roomType: referralResponse?.admissionData?.roomType,
          polyClinic: referralResponse?.admissionData?.polyClinic,
          remarks:
            referralResponse?.remarks ||
            referralResponse?.admissionData?.remarks,
          approxTreatmentCost:
            referralResponse?.admissionData?.approxTreatmentCost,

          patientDetails: referralResponse?.patientDetails || {},
          admissionData: referralResponse?.admissionData || {},
          patientMedicalHistory:
            parseMedicalHistoryData(referralResponse.patientMedicalHistory) ||
            {},
          patientClinicalFindings:
            parseFormSectionData(referralResponse?.patientClinicalFindings) ||
            {},
          patientDiagnosticData: {
            ...referralResponse?.patientDiagnosticData,
            rtaDate: referralResponse?.patientDiagnosticData?.rtaDate
              ? dayjs(
                  referralResponse?.patientDiagnosticData?.rtaDate,
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
          },
          isRta: referralResponse?.patientDiagnosticData?.isRta,
          admissionAilment:
            referralResponse?.patientDiagnosticData?.admissionAilment,
          ailmentDuration:
            referralResponse?.patientDiagnosticData?.ailmentDuration,
          rtaReason: referralResponse?.patientDiagnosticData?.rtaReason,
          rtaDate: referralResponse?.patientDiagnosticData?.rtaDate
            ? dayjs(
                referralResponse?.patientDiagnosticData?.rtaDate,
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          patientProposedTreatmentData:
            parseFormSectionData(
              referralResponse?.patientProposedTreatmentData,
            ) || {},
          provisionalDiagnosis: referralResponse?.casePreAuthEchsDiagnosisList,
        };
        form.setFieldsValue(formValues);
      } else if (emergencyResponse?.id) {
        // Handle emergency response data
        const formValues = {
          ...emergencyResponse,
          type: "emergency",
          beneficiaryId: emergencyResponse?.casePreAuthResponse?.beneficiaryId,
          patientOpdIpdNumber:
            emergencyResponse?.casePreAuthResponse?.patientOpdIpdNumber,
          opdDate:
            emergencyResponse?.admissionData?.opdDate &&
            dayjs(
              emergencyResponse?.admissionData?.opdDate,
              "DD-MM-YYYY HH:mm:ss",
              true,
            ).isValid()
              ? dayjs(
                  emergencyResponse?.admissionData?.opdDate,
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
          polyclinicName: emergencyResponse?.polyclinicName,
          polyClinic:
            emergencyResponse?.admissionData?.polyClinic ||
            emergencyResponse?.polyclinicName ||
            emergencyResponse?.polyClinic,
          remarks:
            emergencyResponse?.remarks ||
            emergencyResponse?.admissionData?.remarks,
          expectedDischargeDate:
            emergencyResponse?.admissionData?.expectedDischargeDate &&
            dayjs(
              emergencyResponse?.admissionData?.expectedDischargeDate,
              "DD-MM-YYYY HH:mm:ss",
              true,
            ).isValid()
              ? dayjs(
                  emergencyResponse.admissionData.expectedDischargeDate,
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
          approxTreatmentCost:
            emergencyResponse?.admissionData?.approxTreatmentCost,
          roomNumber: emergencyResponse?.admissionData?.roomNumber,
          roomType: emergencyResponse?.admissionData?.roomType,
          // polyclinicName: emergencyResponse?.polyclinicName,
          admissionDate:
            emergencyResponse?.admissionDate &&
            dayjs(
              emergencyResponse?.admissionDate,
              "DD-MM-YYYY HH:mm:ss",
              true,
            ).isValid()
              ? dayjs(emergencyResponse?.admissionDate, "DD-MM-YYYY HH:mm:ss")
              : null,
          patientDetails: emergencyResponse?.patientDetails || {},
          admissionData: emergencyResponse?.admissionData || {},
          patientMedicalHistory:
            parseMedicalHistoryData(emergencyResponse.patientMedicalHistory) ||
            {},
          patientClinicalFindings:
            parseFormSectionData(emergencyResponse?.patientClinicalFindings) ||
            {},
          patientDiagnosticData: {
            ...emergencyResponse?.patientDiagnosticData,
            rtaDate: emergencyResponse?.patientDiagnosticData?.rtaDate
              ? dayjs(
                  emergencyResponse?.patientDiagnosticData?.rtaDate,
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
          },
          isRta: emergencyResponse?.patientDiagnosticData?.isRta,
          admissionAilment:
            emergencyResponse?.patientDiagnosticData?.admissionAilment,
          ailmentDuration:
            emergencyResponse?.patientDiagnosticData?.ailmentDuration,
          rtaReason: emergencyResponse?.patientDiagnosticData?.rtaReason,
          rtaDate: emergencyResponse?.patientDiagnosticData?.rtaDate
            ? dayjs(
                emergencyResponse?.patientDiagnosticData?.rtaDate,
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          patientProposedTreatmentData:
            parseFormSectionData(
              emergencyResponse?.patientProposedTreatmentData,
            ) || {},
          provisionalDiagnosis: emergencyResponse?.casePreAuthEchsDiagnosisList,
        };
        form.setFieldsValue(formValues);
      } else {
        // If neither response exists but casePreAuthEchs exists, determine type based on current conditions
        const visitTypeUpper =
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
        const isEmergencyCase =
          visitTypeUpper === "EMERGENCY" || !referralPresent;

        form.setFieldsValue({
          type: isEmergencyCase ? "emergency" : "referral",
          beneficiaryId:
            preAuth?.portalData?.casePreAuthEchs?.casePreAuthResponse
              ?.beneficiaryId,
          patientOpdIpdNumber:
            preAuth?.portalData?.casePreAuthEchs?.casePreAuthResponse
              ?.patientOpdIpdNumber,
          // Set other common fields
          admissionType: visitTypeUpper,
          roomNumber:
            patients?.fetchBasicDetails?.patientResponse?.cardCategory,
          roomType: patients?.fetchBasicDetails?.patientResponse?.cardCategory,
          // roomNumber:
          //   patients?.fetchBasicDetails?.visitInfoResponse?.roomNumber,
          opNumber: patients?.fetchBasicDetails?.visitInfoResponse?.opNumber,
          admissionDate:
            patients?.fetchBasicDetails?.visitInfoResponse?.doa &&
            dayjs(
              patients?.fetchBasicDetails?.visitInfoResponse?.doa,
              "DD-MM-YYYY HH:mm:ss",
              true,
            ).isValid()
              ? dayjs(
                  patients?.fetchBasicDetails?.visitInfoResponse?.doa,
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
        });
      }
    }
  }, [
    preAuth?.portalData?.casePreAuthEchs,
    patients?.fetchBasicDetails,
    referralPresent,
  ]);

  const createReferralPayload = (values: any) => {
    const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
    const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
    const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
    const casePreAuthResponse =
      referralResponse?.casePreAuthResponse ||
      emergencyResponse?.casePreAuthResponse;

    const currentDiagnoses = values?.provisionalDiagnosis || [];
    const allDiagnoses = [
      ...currentDiagnoses?.map((d: any) => ({ ...d, status: true })),
      ...removedDiagnoses,
    ];

    // Process medical history data
    const updatedPatientMedicalHistory = Object.keys(
      values?.patientMedicalHistory || {},
    ).reduce((acc: any, key: string) => {
      if (!key.includes("Value")) {
        const baseValue = values?.patientMedicalHistory[key];
        const inputValue = values?.patientMedicalHistory[`${key}Value`];
        acc[key] = baseValue + ":" + (inputValue || null);
      }
      return acc;
    }, {});

    // Process clinical findings data
    const updatedPatientClinicalFindings = Object.keys(
      values?.patientClinicalFindings || {},
    ).reduce((acc: any, key: string) => {
      if (!key.includes("Value")) {
        const baseValue = values?.patientClinicalFindings[key];
        const inputValue = values?.patientClinicalFindings[`${key}Value`];
        acc[key] = baseValue + ":" + (inputValue || null);
      }
      return acc;
    }, {});

    // Process treatment data
    const updatedPatientProposedTreatmentData = Object.keys(
      values?.patientProposedTreatmentData || {},
    ).reduce((acc: any, key: string) => {
      if (!key.includes("Value")) {
        const baseValue = values?.patientProposedTreatmentData[key];
        const inputValue = values?.patientProposedTreatmentData[`${key}Value`];
        acc[key] = baseValue + ":" + (inputValue || null);
      }
      return acc;
    }, {});

    return {
      schemeMetaDataId:
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
      portalMetaDataId:
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
      casePreAuthId: casePreAuthResponse?.id || 0,
      casePreAuthIhxRequest: null,
      casePreAuthCghsRequest: null,
      casePreAuthVidalRequest: null,
      casePreAuthStarhealthRequest: null,
      casePreAuthEchsRequest: {
        casePreAuthEchsReferralRequest: {
          casePreAuthRequest: {
            beneficiaryId: values?.beneficiaryId,
            patientOpdIpdNumber: values?.patientOpdIpdNumber,
          },
          casePreAuthEchsDiagnosisList: allDiagnoses || [],
          admissionType: values?.admissionType,
          patientDetails: {
            aadharStatus: values?.patientDetails?.aadharStatus || "",
            aadharNumber: values?.patientDetails?.aadharNumber || "",
          },
          admissionData: {
            roomNumber: values?.roomNumber,
            opdDate: values?.opdDate
              ? dayjs(values.opdDate).format("DD-MM-YYYY HH:mm:ss")
              : null,
            expectedDischargeDate: values?.expectedDischargeDate
              ? dayjs(values.expectedDischargeDate).format(
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
            polyClinic: values?.polyClinic,
            remarks: values?.remarks,
            approxTreatmentCost: values?.approxTreatmentCost,
            roomType: values?.roomType,
          },
          // patientMedicalHistory: values?.patientMedicalHistory || {
          //   hypertension: null,
          //   diabetic: null,
          //   heart: null,
          //   copd: null,
          //   asthma: null,
          //   std: null,
          //   alcohol: null,
          //   other: null,
          // },
          // patientClinicalFindings: values?.patientClinicalFindings || {
          //   bp: null,
          //   cvs: null,
          //   rs: null,
          //   abdomen: null,
          //   cns: null,
          //   covid: null,
          //   clinicalOther: null,
          // },
          // patientProposedTreatmentData:
          //   values?.patientProposedTreatmentData || {
          //     investigations: null,
          //     intensiveCare: null,
          //     medicalManagement: null,
          //     surgicalManagement: null,
          //     minorDescription: null,
          //     majorDescription: null,
          //     proposedOther: null,
          //   },

          patientMedicalHistory: updatedPatientMedicalHistory,
          patientClinicalFindings: updatedPatientClinicalFindings,
          patientProposedTreatmentData: updatedPatientProposedTreatmentData,
          referralId: values?.referralId,
          serviceNumber: values?.serviceNumber,
          cardId: values?.cardId,
          opNumber: values?.opNumber,
          admissionDate: values?.admissionDate
            ? dayjs(values.admissionDate).format("DD-MM-YYYY HH:mm:ss")
            : null,
          type: "referral",
          followUpReferral: true,
          remarks: values?.remarks,
          listofResponseUrl: documentUrls,
          patientDocuments: documentUrls,
          patientDiagnosticData: {
            isRta: values?.isRta,
            admissionAilment: values?.admissionAilment,
            ailmentDuration: values?.ailmentDuration,
            rtaReason: values?.rtaReason,
            rtaDate: values?.rtaDate
              ? dayjs(values?.rtaDate, "DD-MM-YYYY HH:mm:ss").format(
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
          },
        },

        echsPreAuthEmergencyRequest: null,
        hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
        schemeId: patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        // listofResponseUrl: documentUrls,
        // patientDocuments: documentUrls,
        // listofResponseUrl: documentUrls,
        // patientDocuments: documentUrls,
      },
      totalAmount: null,
      action: "save",
    };
  };

  const createReferralAcceptanceOnlyPayload = (values: any) => {
    const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
    const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
    const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
    const casePreAuthResponse =
      referralResponse?.casePreAuthResponse ||
      emergencyResponse?.casePreAuthResponse;

    const currentDiagnoses = values?.provisionalDiagnosis || [];
    const allDiagnoses = [
      ...currentDiagnoses?.map((d: any) => ({ ...d, status: true })),
      ...removedDiagnoses,
    ];

    // Process medical history data
    const patientMedicalHistoryData = medicalConditions?.reduce(
      (acc, condition) => ({
        ...acc,
        [condition.key]: `${condition.valuePrefix}:M`,
        [`${condition.key}Value`]: "",
      }),
      {},
    );

    const updatedPatientMedicalHistory = medicalConditions.reduce(
      (acc: any, condition) => {
        const baseValue = condition.valuePrefix;
        const inputValue =
          values?.patientMedicalHistory?.[`${condition.key}Value`] || null;

        acc[condition.key] = `${baseValue}:M:${inputValue}`;
        return acc;
      },
      {},
    );

    const patientClinicalFindingsData = clinicalConditions.reduce(
      (acc, condition) => ({
        ...acc,
        [condition.key]: `${condition.valuePrefix}:M`,
        [`${condition.key}Value`]: "",
      }),
      {},
    );
    // Process clinical findings data
    const updatedPatientClinicalFindings = clinicalConditions.reduce(
      (acc: any, condition) => {
        const baseValue = condition.valuePrefix;
        if (condition.key === "bp") {
          acc[condition.key] = `${baseValue}:Y:130/90`;
          return acc;
        }
        const inputValue =
          values?.patientClinicalFindings?.[`${condition.key}Value`] || null;

        acc[condition.key] = `${baseValue}:M:${inputValue}`;
        return acc;
      },
      {},
    );

    const patientProposedTreatmentData = treatmentConditions?.reduce(
      (acc, condition) => ({
        ...acc,
        [condition.key]: `${condition.valuePrefix}:M`,
        [`${condition.key}Value`]: "",
      }),
      {},
    );
    // Process treatment data
    const updatedPatientProposedTreatmentData = treatmentConditions.reduce(
      (acc: any, condition) => {
        const baseValue = condition.valuePrefix;
        if (condition.key === "medicalManagement") {
          acc[condition.key] = `${baseValue}:Y:supportive treatment`;
          return acc;
        }
        const inputValue =
          values?.patientProposedTreatmentData?.[`${condition.key}Value`] ||
          null;

        acc[condition.key] = `${baseValue}:M:${inputValue}`;
        return acc;
      },
      {},
    );

    return {
      schemeMetaDataId:
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
      portalMetaDataId:
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
      casePreAuthId: casePreAuthResponse?.id || 0,
      casePreAuthIhxRequest: null,
      casePreAuthCghsRequest: null,
      casePreAuthVidalRequest: null,
      casePreAuthStarhealthRequest: null,
      casePreAuthEchsRequest: {
        casePreAuthEchsReferralRequest: {
          casePreAuthRequest: {
            beneficiaryId: values?.beneficiaryId,
            patientOpdIpdNumber: values?.patientOpdIpdNumber,
          },
          referralAcceptanceOnly: true,
          fromFo: auth?.role?.role === "FO_STAFF" ? true : false,
          casePreAuthEchsDiagnosisList: allDiagnoses || [],
          admissionType: values?.admissionType,
          patientDetails: {
            aadharStatus: values?.patientDetails?.aadharStatus || "",
            aadharNumber: values?.patientDetails?.aadharNumber || "",
          },
          admissionData: {
            roomNumber: values?.roomNumber,
            opdDate: values?.opdDate
              ? dayjs(values.opdDate).format("DD-MM-YYYY HH:mm:ss")
              : null,
            expectedDischargeDate: values?.expectedDischargeDate
              ? dayjs(values.expectedDischargeDate).format(
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
            polyClinic: values?.polyClinic,
            remarks: values?.remarks,
            approxTreatmentCost: values?.approxTreatmentCost,
            roomType: values?.roomType,
          },
          // patientMedicalHistory: values?.patientMedicalHistory || {
          //   hypertension: null,
          //   diabetic: null,
          //   heart: null,
          //   copd: null,
          //   asthma: null,
          //   std: null,
          //   alcohol: null,
          //   other: null,
          // },
          // patientClinicalFindings: values?.patientClinicalFindings || {
          //   bp: null,
          //   cvs: null,
          //   rs: null,
          //   abdomen: null,
          //   cns: null,
          //   covid: null,
          //   clinicalOther: null,
          // },
          // patientProposedTreatmentData:
          //   values?.patientProposedTreatmentData || {
          //     investigations: null,
          //     intensiveCare: null,
          //     medicalManagement: null,
          //     surgicalManagement: null,
          //     minorDescription: null,
          //     majorDescription: null,
          //     proposedOther: null,
          //   },

          patientMedicalHistory: updatedPatientMedicalHistory,
          patientClinicalFindings: updatedPatientClinicalFindings,
          patientProposedTreatmentData: updatedPatientProposedTreatmentData,
          referralId: values?.referralId,
          serviceNumber: values?.serviceNumber,
          cardId: values?.cardId,
          opNumber: values?.opNumber,
          admissionDate: values?.admissionDate
            ? dayjs(values.admissionDate).format("DD-MM-YYYY HH:mm:ss")
            : null,
          type: "referral",
          followUpReferral: true,
          remarks: values?.remarks,
          listofResponseUrl: documentUrls,
          patientDocuments: documentUrls,
          patientDiagnosticData: {
            isRta: values?.isRta,
            admissionAilment: values?.admissionAilment,
            ailmentDuration: values?.ailmentDuration,
            rtaReason: values?.rtaReason,
            rtaDate: values?.rtaDate
              ? dayjs(values?.rtaDate, "DD-MM-YYYY HH:mm:ss").format(
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
          },
        },

        echsPreAuthEmergencyRequest: null,
        hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
        schemeId: patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        // listofResponseUrl: documentUrls,
        // patientDocuments: documentUrls,
        // listofResponseUrl: documentUrls,
        // patientDocuments: documentUrls,
      },
      totalAmount: null,
      action: "save",
      referralAcceptanceOnly: true,
      fromFo: auth?.role?.role === "FO_STAFF" ? true : false,
    };
  };

  const createEmergencyPayload = (values: any) => {
    const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
    const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
    const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
    const casePreAuthResponse =
      referralResponse?.casePreAuthResponse ||
      emergencyResponse?.casePreAuthResponse;

    const currentDiagnoses = values?.provisionalDiagnosis || [];
    const allDiagnoses = [
      ...currentDiagnoses?.map((d: any) => ({ ...d, status: true })),
      ...removedDiagnoses,
    ];

    // Process medical history data
    const updatedPatientMedicalHistory = Object.keys(
      values?.patientMedicalHistory || {},
    ).reduce((acc: any, key: string) => {
      if (!key.includes("Value")) {
        const baseValue = values?.patientMedicalHistory[key];
        const inputValue = values?.patientMedicalHistory[`${key}Value`];
        acc[key] = baseValue + ":" + (inputValue || null);
      }
      return acc;
    }, {});

    // Process clinical findings data
    const updatedPatientClinicalFindings = Object.keys(
      values?.patientClinicalFindings || {},
    ).reduce((acc: any, key: string) => {
      if (!key.includes("Value")) {
        const baseValue = values?.patientClinicalFindings[key];
        const inputValue = values?.patientClinicalFindings[`${key}Value`];
        acc[key] = baseValue + ":" + (inputValue || null);
      }
      return acc;
    }, {});

    // Process treatment data
    const updatedPatientProposedTreatmentData = Object.keys(
      values?.patientProposedTreatmentData || {},
    ).reduce((acc: any, key: string) => {
      if (!key.includes("Value")) {
        const baseValue = values?.patientProposedTreatmentData[key];
        const inputValue = values?.patientProposedTreatmentData[`${key}Value`];
        acc[key] = baseValue + ":" + (inputValue || null);
      }
      return acc;
    }, {});

    return {
      schemeMetaDataId:
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
      portalMetaDataId:
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
      casePreAuthId: casePreAuthResponse?.id || 0,
      casePreAuthIhxRequest: null,
      casePreAuthCghsRequest: null,
      casePreAuthVidalRequest: null,
      casePreAuthStarhealthRequest: null,
      casePreAuthEchsRequest: {
        casePreAuthEchsReferralRequest: null,
        echsPreAuthEmergencyRequest: {
          casePreAuthRequest: {
            beneficiaryId: values?.beneficiaryId,
            patientOpdIpdNumber: values?.patientOpdIpdNumber,
          },
          casePreAuthEchsDiagnosisList: allDiagnoses || [],
          admissionType: values?.admissionType,
          patientDetails: {
            aadharStatus: values?.patientDetails?.aadharStatus || "",
            aadharNumber: values?.patientDetails?.aadharNumber || "",
          },
          admissionData: {
            ...values?.admissionData,
            roomNumber: values?.roomNumber,
            approxTreatmentCost: values?.approxTreatmentCost,
            opdDate: values?.opdDate
              ? dayjs(values.opdDate).format("DD-MM-YYYY HH:mm:ss")
              : null,
            expectedDischargeDate: values?.expectedDischargeDate
              ? dayjs(values.expectedDischargeDate).format(
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
            // polyClinic: values?.polyClinic,
            remarks: values?.remarks,
            roomType: values?.roomType,
          },
          polyclinicName: values?.polyclinicName,
          patientMedicalHistory: updatedPatientMedicalHistory,
          patientClinicalFindings: updatedPatientClinicalFindings,
          patientProposedTreatmentData: updatedPatientProposedTreatmentData,
          serviceNumber: values?.serviceNumber,
          opNumber: values?.opNumber,
          admissionDate: values?.admissionDate
            ? dayjs(values.admissionDate).format("DD-MM-YYYY HH:mm:ss")
            : null,
          type: "emergency",
          remarks: values?.remarks,
          portal: "echs",
          gsproviderId: 1,
          patientDocuments: documentUrls,
          listofResponseUrl: documentUrls,
          patientDiagnosticData: {
            isRta: values?.isRta,
            admissionAilment: values?.admissionAilment,
            ailmentDuration: values?.ailmentDuration,
            rtaReason: values?.rtaReason,
            rtaDate: values?.rtaDate
              ? dayjs(values?.rtaDate, "DD-MM-YYYY HH:mm:ss").format(
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
          },
        },
        hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
        schemeId: patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
      },
      totalAmount: null,
      action: "save",
    };
  };

  const createEmergencyAcceptancePayload = (values: any) => {
    const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
    const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
    const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
    const casePreAuthResponse =
      referralResponse?.casePreAuthResponse ||
      emergencyResponse?.casePreAuthResponse;

    const currentDiagnoses = values?.provisionalDiagnosis || [];
    const allDiagnoses = [
      ...currentDiagnoses?.map((d: any) => ({ ...d, status: true })),
      ...removedDiagnoses,
    ];

    const patientMedicalHistoryData = medicalConditions?.reduce(
      (acc, condition) => ({
        ...acc,
        [condition.key]: `${condition.valuePrefix}:M`,
        [`${condition.key}Value`]: "",
      }),
      {},
    );
    // Process medical history data
    const updatedPatientMedicalHistory = medicalConditions.reduce(
      (acc: any, condition) => {
        const baseValue = condition.valuePrefix;
        const inputValue =
          values?.patientMedicalHistory?.[`${condition.key}Value`] || null;

        acc[condition.key] = `${baseValue}:M:${inputValue}`;
        return acc;
      },
      {},
    );

    const patientClinicalFindingsData = clinicalConditions.reduce(
      (acc, condition) => ({
        ...acc,
        [condition.key]: `${condition.valuePrefix}:M`,
        [`${condition.key}Value`]: "",
      }),
      {},
    );
    // Process clinical findings data
    // const updatedPatientClinicalFindings = Object.keys(
    //   patientClinicalFindingsData || values?.patientClinicalFindings || {},
    // ).reduce((acc: any, key: string) => {
    //   if (!key.includes("Value")) {
    //     const baseValue = values?.patientClinicalFindings[key];
    //     const inputValue = values?.patientClinicalFindings[`${key}Value`];
    //     acc[key] = baseValue + ":" + (inputValue || null);
    //   }
    //   return acc;
    // }, {});
    const updatedPatientClinicalFindings = clinicalConditions.reduce(
      (acc: any, condition) => {
        const baseValue = condition.valuePrefix;
        if (condition.key === "bp") {
          acc[condition.key] = `${baseValue}:Y:130/90`;
          return acc;
        }
        const inputValue =
          values?.patientClinicalFindings?.[`${condition.key}Value`] || null;

        acc[condition.key] = `${baseValue}:M:${inputValue}`;
        return acc;
      },
      {},
    );

    const patientProposedTreatmentData = treatmentConditions.reduce(
      (acc, condition) => ({
        ...acc,
        [condition.key]: `${condition.valuePrefix}:M`,
        [`${condition.key}Value`]: "",
      }),
      {},
    );
    // Process treatment data
    // const updatedPatientProposedTreatmentData = Object.keys(
    //   patientProposedTreatmentData ||
    //     values?.patientProposedTreatmentData ||
    //     {},
    // ).reduce((acc: any, key: string) => {
    //   if (!key.includes("Value")) {
    //     const baseValue = values?.patientProposedTreatmentData[key];
    //     const inputValue = values?.patientProposedTreatmentData[`${key}Value`];
    //     acc[key] = baseValue + ":" + (inputValue || null);
    //   }
    //   return acc;
    // }, {});

    const updatedPatientProposedTreatmentData = treatmentConditions.reduce(
      (acc: any, condition) => {
        const baseValue = condition.valuePrefix;
        if (condition.key === "medicalManagement") {
          acc[condition.key] = `${baseValue}:Y:supportive treatment`;
          return acc;
        }
        const inputValue =
          values?.patientProposedTreatmentData?.[`${condition.key}Value`] ||
          null;

        acc[condition.key] = `${baseValue}:M:${inputValue}`;
        return acc;
      },
      {},
    );

    return {
      schemeMetaDataId:
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
      portalMetaDataId:
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
      casePreAuthId: casePreAuthResponse?.id || 0,
      casePreAuthIhxRequest: null,
      casePreAuthCghsRequest: null,
      casePreAuthVidalRequest: null,
      casePreAuthStarhealthRequest: null,
      casePreAuthEchsRequest: {
        casePreAuthEchsReferralRequest: null,
        echsPreAuthEmergencyRequest: {
          casePreAuthRequest: {
            beneficiaryId: values?.beneficiaryId,
            patientOpdIpdNumber: values?.patientOpdIpdNumber,
          },
          referralAcceptanceOnly: true,
          fromFo: auth?.role?.role === "FO_STAFF" ? true : false,
          casePreAuthEchsDiagnosisList: allDiagnoses || [],
          admissionType: values?.admissionType,
          patientDetails: {
            aadharStatus: values?.patientDetails?.aadharStatus || "",
            aadharNumber: values?.patientDetails?.aadharNumber || "",
          },
          admissionData: {
            ...values?.admissionData,
            roomNumber: values?.roomNumber,
            approxTreatmentCost: values?.approxTreatmentCost,
            opdDate: values?.opdDate
              ? dayjs(values.opdDate).format("DD-MM-YYYY HH:mm:ss")
              : null,
            expectedDischargeDate: values?.expectedDischargeDate
              ? dayjs(values.expectedDischargeDate).format(
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
            polyClinic: values?.polyClinic,
            remarks: values?.remarks,
            roomType: values?.roomType,
          },
          polyclinicName: values?.polyclinicName || values?.polyClinic,
          patientMedicalHistory: updatedPatientMedicalHistory,
          patientClinicalFindings: updatedPatientClinicalFindings,
          patientProposedTreatmentData: updatedPatientProposedTreatmentData,
          serviceNumber: values?.serviceNumber,
          opNumber: values?.opNumber,
          admissionDate: values?.admissionDate
            ? dayjs(values.admissionDate).format("DD-MM-YYYY HH:mm:ss")
            : null,
          type: "emergency",
          remarks: values?.remarks,
          portal: "echs",
          gsproviderId: 1,
          patientDocuments: documentUrls,
          listofResponseUrl: documentUrls,
          patientDiagnosticData: {
            isRta: values?.isRta,
            admissionAilment: values?.admissionAilment,
            ailmentDuration: values?.ailmentDuration,
            rtaReason: values?.rtaReason,
            rtaDate: values?.rtaDate
              ? dayjs(values?.rtaDate, "DD-MM-YYYY HH:mm:ss").format(
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
          },
        },
        hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
        schemeId: patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
      },
      totalAmount: null,
      action: "save",
      referralAcceptanceOnly: true,
      fromFo: auth?.role?.role === "FO_STAFF" ? true : false,
    };
  };

  const validateRequiredDocuments = (
    mandatoryDocuments: any,
    documentUrls: any,
  ) => {
    for (const doc of mandatoryDocuments) {
      if (doc.required & doc.visible) {
        const isUploaded = documentUrls?.some(
          (uploadedDoc: any) => uploadedDoc.documentLabelType === doc.label,
        );
        if (!isUploaded) {
          return false;
        }
      }
    }
    return true;
  };

  const handleSaveAsDraft = async () => {
    try {
      // await form.validateFields();
      const values = await form.getFieldsValue(true);
      setIsSaving(true);

      const visitTypeUpper =
        patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
      const isEmergencyCase =
        visitTypeUpper === "EMERGENCY" || !referralPresent;
      const formData = isEmergencyCase
        ? createEmergencyPayload(values)
        : createReferralPayload(values);

      if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
        const payload = {
          schemeMetaDataId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          portalMetaDataId:
            patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
          casePreAuthId: preAuth?.portalData?.casePreAuthEchs?.id || 0,
          casePreAuthEchsRequest: formData,
          casePreAuthIhxRequest: null,
          casePreAuthCghsRequest: null,
          casePreAuthVidalRequest: null,
          casePreAuthStarhealthRequest: null,
          action: "save",
        };

        const resp = await saveDraftEchs(
          patients?.fetchBasicDetails?.casesResponse?.id,
          patients?.fetchBasicDetails?.visitInfoResponse?.id,
          formData,
        );

        if (resp) {
          successMessage("PreAuth Saved Successfully");
          await refreshData();
        }
      }
    } catch (err: any) {
      handleError(err);
    } finally {
      setIsSaving(false);
      setConfirmationModal(false);
    }
  };

  const handleUpdateAsDraft = async () => {
    try {
      // await form.validateFields();
      const values = await form.getFieldsValue(true);
      setIsSaving(true);

      const visitTypeUpper =
        patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
      const isEmergencyCase =
        visitTypeUpper === "EMERGENCY" || !referralPresent;
      const formData = isEmergencyCase
        ? createEmergencyPayload(values)
        : createReferralPayload(values);

      if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
        const payload = {
          schemeMetaDataId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          portalMetaDataId:
            patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
          casePreAuthEchsRequest: formData,
          action: "save",
        };

        const resp = await updateDraftEchs(
          patients?.fetchBasicDetails?.casesResponse?.id,
          patients?.fetchBasicDetails?.visitInfoResponse?.id,
          preAuth.portalData?.id,
          formData,
        );

        if (resp) {
          successMessage("PreAuth Updated Successfully");
          await refreshData();
        }
      }
    } catch (err: any) {
      handleError(err);
    } finally {
      setIsSaving(false);
      setConfirmationModal(false);
    }
  };

  const handleIntimate = async () => {
    try {
      // const values = await form.validateFields();
      const values = await form.getFieldsValue(true);
      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        setIsIntimating(true);

        const visitTypeUpper =
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
        const isEmergencyCase =
          visitTypeUpper === "EMERGENCY" || !referralPresent;
        const formData = isEmergencyCase
          ? createEmergencyPayload(values)
          : createReferralPayload(values);

        if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
          const payload = {
            schemeMetaDataId:
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0] || 0,
            casePreAuthId: preAuth?.portalData?.casePreAuthEchs?.id || 0,
            casePreAuthEchsRequest: formData,
            action: "intimate",
          };

          const resp = await saveDraftEchs(
            patients?.fetchBasicDetails?.casesResponse?.id,
            patients?.fetchBasicDetails?.visitInfoResponse?.id,
            { ...formData, action: "intimate" },
          );

          if (resp) {
            successMessage("PreAuth Sent To QC Successfully");
            await refreshData();
          }
        }
      } else {
        errorMessageModal("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsIntimating(false);
      }
    } catch (err: any) {
      handleError(err);
    } finally {
      setIsIntimating(false);
      setConfirmationModal(false);
    }
  };

  const handleSubmitOnPortal = async () => {
    if (patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal) {
      try {
        const values = await form.validateFields();
        if (
          validateRequiredDocuments(
            documentRequestResponseDocuments["requestedDocuments"],
            documentUrls,
          )
        ) {
          setIsSubmitting(true);

          const visitTypeUpper =
            patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
          const isEmergencyCase =
            visitTypeUpper === "EMERGENCY" || !referralPresent;
          const formData = isEmergencyCase
            ? createEmergencyPayload(values)
            : createReferralPayload(values);

          if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
            const payload = {
              schemeMetaDataId:
                patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
              portalMetaDataId:
                patients?.fetchBasicDetails?.casesResponse
                  ?.portalMetaDataIds?.[0],
              casePreAuthId: preAuth?.portalData?.casePreAuthEchs?.id || 0,
              casePreAuthEchsRequest: formData,
              integratedWithPortal:
                patients?.fetchBasicDetails?.casesResponse
                  ?.integratedWithPortal,
            };

            const resp = await fetchPreAuthToPortalEchs(
              patients?.fetchBasicDetails?.casesResponse?.id,
              patients?.fetchBasicDetails?.visitInfoResponse?.id,
              {
                ...formData,
                action: "submit",
                casePreAuthEchsRequest: {
                  ...formData?.casePreAuthEchsRequest,
                  manualPreAuth: false,
                  sendToPortal: true,
                },
              },
            );

            if (resp) {
              successMessage("PreAuth Submitted Successfully");
              await refreshData();
            }
          }
        } else {
          errorMessageModal("Please, Upload all required documents");
          setConfirmationModal(false);
          setIsSubmitting(false);
        }
      } catch (err: any) {
        handleError(err);
      } finally {
        setIsSubmitting(false);
        setSubmissionModal(false);
        setIsSubmittedOnPortal(false);
        setConfirmationModal(false);
      }
    } else {
      setManuallyClaimIdPortal(true);
    }
  };

  const handleSaveModalOption = async () => {
    try {
      const values = await form.validateFields();
      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        setIsSubmittedOnPortal(true);
        setIsSubmitting(true);
        const visitTypeUpper =
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
        const isEmergencyCase =
          visitTypeUpper === "EMERGENCY" || !referralPresent;
        const formData = isEmergencyCase
          ? createEmergencyPayload(values)
          : createReferralPayload(values);

        if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
          const payload = {
            schemeMetaDataId:
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            casePreAuthId: preAuth?.portalData?.casePreAuthEchs?.id || 0,
            casePreAuthEchsRequest: formData,
            integratedWithPortal:
              patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
          };

          const resp = await fetchPreAuthToPortalEchs(
            patients?.fetchBasicDetails?.casesResponse?.id,
            patients?.fetchBasicDetails?.visitInfoResponse?.id,
            {
              ...formData,
              action: "submit",
              integratedWithPortal:
                patients?.fetchBasicDetails?.casesResponse
                  ?.integratedWithPortal,
              casePreAuthEchsRequest: {
                ...formData?.casePreAuthEchsRequest,
                manualPreAuth: false,
                sendToPortal: true,
              },
            },
          );

          if (resp) {
            successMessage("PreAuth Submitted Successfully");
            setManuallyClaimIdPortal(false);
            await refreshData();
          }
        }
      } else {
        errorMessageModal("Please, Upload All Required Documents");
        setSubmissionModal(false);
        setIsSubmittedOnPortal(false);
        setIsSubmitting(false);
        return;
      }
    } catch (err: any) {
      handleError(err);
      setIsLoading(false);
    }
    setSubmissionModal(false);
    setIsSubmittedOnPortal(false);
    setIsSubmitting(false);
  };

  const handleSaveManually = async (idValues: any) => {
    try {
      const values = await form.validateFields();
      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        setIsSubmitting(true);

        const visitTypeUpper =
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
        const isEmergencyCase =
          visitTypeUpper === "EMERGENCY" || !referralPresent;
        const formData = isEmergencyCase
          ? createEmergencyPayload(values)
          : createReferralPayload(values);

        if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
          const payload = {
            schemeMetaDataId:
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            casePreAuthId: preAuth?.portalData?.casePreAuthEchs?.id || 0,
            casePreAuthEchsRequest: formData,
            integratedWithPortal:
              patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
          };

          const resp = await fetchPreAuthToPortalEchs(
            patients?.fetchBasicDetails?.casesResponse?.id,
            patients?.fetchBasicDetails?.visitInfoResponse?.id,
            {
              ...formData,
              action: "submit",
              integratedWithPortal:
                patients?.fetchBasicDetails?.casesResponse
                  ?.integratedWithPortal,
              casePreAuthEchsRequest: {
                ...formData?.casePreAuthEchsRequest,
                manualPreAuth: true,
                sendToPortal: false,
                claimId: idValues?.manualId,
              },
            },
          );

          if (resp) {
            successMessage("PreAuth Submitted Successfully");
            setManuallyClaimIdPortal(false);
            await refreshData();
          }
        }
      } else {
        errorMessageModal("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsSubmitting(false);
        setManuallyClaimIdPortal(false);
      }
    } catch (err: any) {
      handleError(err);
      setManuallyClaimIdPortal(false);
    } finally {
      setIsSubmitting(false);
      setSubmissionModal(false);
      setIsSubmittedOnPortal(false);
      setConfirmationModal(false);
      setManuallyClaimIdPortal(false);
    }
  };

  const handleSaveManuallyOnPortal = async () => {
    try {
      const values = await form.validateFields();
      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        setIsSubmitting(true);
        const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
        const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
        const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
        const claimId = referralResponse?.claimId || emergencyResponse?.claimId;

        const visitTypeUpper =
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
        const isEmergencyCase =
          visitTypeUpper === "EMERGENCY" || !referralPresent;
        const formData = isEmergencyCase
          ? createEmergencyPayload(values)
          : createReferralPayload(values);

        if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
          const payload = {
            schemeMetaDataId:
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            casePreAuthId: preAuth?.portalData?.casePreAuthEchs?.id || 0,
            casePreAuthEchsRequest: formData,
            integratedWithPortal:
              patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
          };

          const resp = await fetchPreAuthToPortalEchs(
            patients?.fetchBasicDetails?.casesResponse?.id,
            patients?.fetchBasicDetails?.visitInfoResponse?.id,
            {
              ...formData,
              action: "submit",
              integratedWithPortal:
                patients?.fetchBasicDetails?.casesResponse
                  ?.integratedWithPortal,
              casePreAuthEchsRequest: {
                ...formData?.casePreAuthEchsRequest,
                manualPreAuth: false,
                sendToPortal: true,
                claimId: claimId,
              },
            },
          );

          if (resp) {
            successMessage("PreAuth Submitted Successfully");
            await refreshData();
          }
        }
      } else {
        errorMessageModal("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsSubmitting(false);
        setIsSubmitOnPortal(false);
      }
    } catch (err: any) {
      handleError(err);
    } finally {
      setIsSubmitting(false);
      setIsSubmitOnPortal(false);
    }
  };

  const [isSubmittingNotOnPortal, setIsSubmittingNotOnPortal] =
    useState<boolean>(false);

  const handleSaveManuallyNotOnPortal = async () => {
    try {
      const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
      const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
      const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
      const claimId = referralResponse?.claimId || emergencyResponse?.claimId;

      const values = await form.validateFields();
      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        setIsSubmittingNotOnPortal(true);

        const visitTypeUpper =
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
        const isEmergencyCase =
          visitTypeUpper === "EMERGENCY" || !referralPresent;
        const formData = isEmergencyCase
          ? createEmergencyPayload(values)
          : createReferralPayload(values);

        if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
          const payload = {
            schemeMetaDataId:
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            casePreAuthId: preAuth?.portalData?.casePreAuthEchs?.id || 0,
            casePreAuthEchsRequest: formData,
            integratedWithPortal:
              patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
          };

          const resp = await fetchPreAuthToPortalEchs(
            patients?.fetchBasicDetails?.casesResponse?.id,
            patients?.fetchBasicDetails?.visitInfoResponse?.id,
            {
              ...formData,
              action: "submit",
              integratedWithPortal:
                patients?.fetchBasicDetails?.casesResponse
                  ?.integratedWithPortal,
              casePreAuthEchsRequest: {
                ...formData?.casePreAuthEchsRequest,
                manualPreAuth: true,
                sendToPortal: false,
                claimId: claimId,
              },
            },
          );

          if (resp) {
            successMessage("PreAuth Submitted Successfully");
            setIsSubmittingNotOnPortal(false);
            await refreshData();
          }
        }
      } else {
        errorMessageModal("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsSubmittingNotOnPortal(false);
        setIsSubmitOnPortal(false);
      }
    } catch (err: any) {
      handleError(err);
      setIsSubmitOnPortal(false);
    } finally {
      setIsSubmittingNotOnPortal(false);
      setIsSubmitOnPortal(false);
    }
  };

  const handleSaveManuallyForQc = async (idValues: any) => {
    try {
      const values = await form.getFieldsValue(true);
      if (
        true
        // validateRequiredDocuments(
        //   documentRequestResponseDocuments["requestedDocuments"],
        //   documentUrls,
        // )
      ) {
        setIsLoadingForQc(true);

        const visitTypeUpper =
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
        const isEmergencyCase =
          visitTypeUpper === "EMERGENCY" || !referralPresent;
        const formData = isEmergencyCase
          ? createEmergencyAcceptancePayload(values)
          : createReferralAcceptanceOnlyPayload(values);

        if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
          const payload = {
            schemeMetaDataId:
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            casePreAuthId: preAuth?.portalData?.casePreAuthEchs?.id || 0,
            casePreAuthEchsRequest: formData,
            integratedWithPortal:
              patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
          };

          const resp = await saveDraftEchs(
            patients?.fetchBasicDetails?.casesResponse?.id,
            patients?.fetchBasicDetails?.visitInfoResponse?.id,
            {
              ...formData,
              action: "intimate",
              referralAcceptanceOnly: true,
              fromFo: auth?.role?.role === "FO_STAFF" ? true : false,
              integratedWithPortal:
                patients?.fetchBasicDetails?.casesResponse
                  ?.integratedWithPortal,
              casePreAuthEchsRequest: {
                ...formData?.casePreAuthEchsRequest,
                claimId: idValues?.manualId,
              },
            },
          );

          if (resp) {
            successMessage(
              "Claim Id Generation Manually Submitted Successfully",
            );
            await refreshData();
          }
        }
      }
      //   else {
      //     errorMessage("Please, Upload all required documents");
      //     setConfirmationModal(false);
      //     setIsLoadingForQc(false);
      //   }
    } catch (err: any) {
      handleError(err);
      setIsManuallyEnteredIdForQc(false);
    } finally {
      setIsLoadingForQc(false);
      setIsManuallyEnteredIdForQc(false);
    }
  };

  const handleManuallyIdEnter = () => {
    setIsSubmittedOnPortal(false);
    setIsManuallyEnteredId(true);
  };

  const handleManuallyIdEnterForQc = () => {
    setIsClaimIdPresent(false);
    // setIsManuallyEnteredId(true);
    setIsManuallyEnteredIdForQc(true);
  };

  const handleCancelSubmissionPortal = () => {
    setIsSubmittedOnPortal(false);
    setSubmissionModal(true);
  };

  const handleCancelSubmissionForQc = () => {
    setIsClaimIdPresent(false);
    // if (
    //   patients?.fetchBasicDetails?.patientResponse?.age &&
    //   patients?.fetchBasicDetails?.patientResponse?.age < 70 &&
    //   patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() !==
    //     "EMERGENCY"
    // ) {
    //   setIsGenerateClaimId(true);
    // } else {
    //   setSentForQcModal(true);
    // }
    setIsGenerateClaimId(true);
  };

  const handleCancelManuallyId = () => {
    setIsManuallyEnteredId(false);
  };

  const handleOk = () => {
    setSubmissionModal(false);
  };

  const handleRegistrationCancel = () => {
    setSubmissionModal(false);
    setIsSubmittedOnPortal(false);
  };

  const handleRegistrationCancelForQc = () => {
    setIsClaimIdPresent(false);
    setSentForQcModal(false);
  };

  const handleCancelGenerateClaimId = () => {
    setIsGenerateClaimId(false);
    // setSentForQcModal(true);
  };

  const handleSaveGenerateClaimId = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);
      if (
        true
        // validateRequiredDocuments(
        //   documentRequestResponseDocuments["requestedDocuments"],
        //   documentUrls,
        // )
      ) {
        setIsGenerateClaimIdLoading(true);

        const visitTypeUpper =
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
        const isEmergencyCase =
          visitTypeUpper === "EMERGENCY" || !referralPresent;
        const formData = isEmergencyCase
          ? createEmergencyAcceptancePayload(values)
          : createReferralAcceptanceOnlyPayload(values);

        if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
          const payload = {
            schemeMetaDataId:
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0] || 0,
            casePreAuthId: preAuth?.portalData?.casePreAuthEchs?.id || 0,
            casePreAuthEchsRequest: formData,
            action: "intimate",
          };

          const resp = await saveDraftEchs(
            patients?.fetchBasicDetails?.casesResponse?.id,
            patients?.fetchBasicDetails?.visitInfoResponse?.id,
            {
              ...formData,
              action: "intimate",
              referralAcceptanceOnly: true,
              fromFo: auth?.role?.role === "FO_STAFF" ? true : false,
              integratedWithPortal:
                patients?.fetchBasicDetails?.casesResponse
                  ?.integratedWithPortal,
              casePreAuthEchsRequest: {
                ...formData?.casePreAuthEchsRequest,
                manualPreAuth: false,
                sendToPortal: true,
              },
            },
          );

          if (resp) {
            successMessage("Claim Id Sent For Generation Successfully");
            await refreshData();
          }
        }
      }
      //   else {
      //     errorMessage("Please, Upload all required documents");
      //     setConfirmationModal(false);
      //     setIsGenerateClaimIdLoading(false);
      //   }
    } catch (err: any) {
      handleError(err);
    } finally {
      setIsGenerateClaimIdLoading(false);
      setConfirmationModal(false);
    }
  };

  const handleApprovePreAuth = async () => {
    try {
      const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
      const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
      const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
      const casePreAuthResponse =
        referralResponse?.casePreAuthResponse ||
        emergencyResponse?.casePreAuthResponse;
      const values = await form.validateFields();

      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        setIsApprovedInitiated(true);

        const visitTypeUpper =
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase();
        const isEmergencyCase =
          visitTypeUpper === "EMERGENCY" || !referralPresent;
        const formData = isEmergencyCase
          ? createEmergencyPayload(values)
          : createReferralPayload(values);

        const updatedFormData = {
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          stage: "PreAuth",
          approved: true,
          casePreAuthId: casePreAuthResponse?.id || 0,
          preAuthRequest: formData,
        };

        const res = await submitApprovalEchs(updatedFormData);
        setIsApproved(true);
        setIsApprovedInitiated(false);
        setConfirmationModal(false);
        message.success("PreAuth Approved Successfully");
        refreshData();
        setConfirmationModal(false);
      } else {
        errorMessageModal("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsApprovedInitiated(false);
        setIsApproved(false);
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessageModal("Please, fill all details before submitting");
        setIsApprovedInitiated(false);
        setIsApproved(false);
      } else {
        errorMessageModal("Please, Try again later");
        setIsApprovedInitiated(false);
        setIsApproved(false);
      }
      setConfirmationModal(false);
    }
  };

  const handleSubmit = async () => {
    if (patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal) {
      try {
        const values = await form.validateFields();
        const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
        const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
        const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
        const claimId = referralResponse?.claimId || emergencyResponse?.claimId;
        if (claimId) {
          setIsSubmitOnPortal(true);
        } else {
          setIsSubmittedOnPortal(true);
        }
      } catch (err: any) {
        if (err?.errorFields) {
          errorMessageModal(
            "Please fill all required fields before submitting",
          );
        } else {
          errorMessageModal("Please try again later");
        }
      }
    } else {
      setManuallyClaimIdPortal(true);
    }
  };

  const handleSentToQC = async () => {
    if (patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal) {
      try {
        // const values = await form.validateFields();
        setIsClaimIdPresent(true);
      } catch (err: any) {
        if (err?.errorFields) {
          errorMessageModal(
            "Please fill all required fields before submitting",
          );
        } else {
          errorMessageModal("Please try again later");
        }
      }
    } else {
      setManuallyClaimIdPortal(true);
    }
  };

  const handleSaveConfirmationModal = async (state: string) => {
    if (state === "intimate") {
      if (auth?.role?.role !== "FO_STAFF") {
        try {
          await form.validateFields();
        } catch (err: any) {
          handleError(err);
          return;
        }
      }
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
    }

    setConfirmationModal(true);
    setConfirmationState(state);

    switch (state) {
      case "save":
        setTitle("Do you want to save PreAuth?");
        break;
      case "update":
        setTitle("Do you want to update PreAuth?");
        break;
      case "intimate":
        setTitle("Do you want to send PreAuth for QC?");
        break;
      case "submit":
        setTitle("Do you want to submit PreAuth?");
        break;
      case "approve":
        setTitle("Do you want to approve PreAuth?");
        break;
    }
  };

  const handleSave = async () => {
    switch (confirmationState) {
      case "save":
        await handleSaveAsDraft();
        break;
      case "update":
        await handleUpdateAsDraft();
        break;
      case "intimate":
        await handleIntimate();
        break;
      case "submit":
        await handleSubmitOnPortal();
        // await handleSubmit();
        break;
      case "approve":
        await handleApprovePreAuth();
        break;
    }
  };

  const refreshData = async () => {
    setConfirmationModal(false);
    dispatch(
      getPortalDataEchs({
        caseId: patients?.fetchBasicDetails?.casesResponse?.id || 0,
        visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id || 0,
      }),
    );
    await getTimelineDetail();
    await fetchStepperForStatus();
    dispatch(
      getBasicDetails({
        id: patients?.fetchBasicDetails?.visitInfoResponse?.id || 0,
      }),
    );
  };

  const getTimelineDetail = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      await dispatch(
        getTimelineDetails({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        }),
      );
    }
  };

  const fetchStepperForStatus = async () => {
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      dispatch(
        fetchStepperData({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        }),
      );
    }
  };

  const handleError = (err: any) => {
    if (err?.errorFields) {
      errorMessageModal("Please fill all required fields");
    } else {
      errorMessageModal(err?.message || "Please try again later");
    }
  };

  const handleAttachDocuments = async (updatedDocumentUrls: any) => {
    const payloadSchema: {
      casePreAuthAttachments: any[];
      caseEnhancementsAttachments: any[];
      caseDischargeAttachments: any[];
      caseClaimAttachments: any[];
    } = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
    };

    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;

    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    const schemeMetaDataId =
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;

    payloadSchema["casePreAuthAttachments"] = updatedDocumentUrls;

    if (caseId && visitId && schemeMetaDataId) {
      try {
        const AllDocumentList = await updateAllDocumentsData(
          caseId,
          visitId,
          payloadSchema,
        );
        message.success(`documents saved successfully`);
        fetchDocuments();
      } catch (error) {
        message.error(`Something went wrong`);
      }
    }
  };

  const handleSaveData = async () => {
    const payloadSchema: {
      casePreAuthAttachments: any[];
      caseEnhancementsAttachments: any[];
      caseDischargeAttachments: any[];
      caseClaimAttachments: any[];
    } = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
    };

    const updatedDocumentUrls: any = documentUrls?.filter((document: any) =>
      documentRequestResponseDocuments["responseDocuments"]?.some(
        (doc: any) => doc?.label === document?.documentLabelType,
      ),
    );

    payloadSchema["casePreAuthAttachments"] = updatedDocumentUrls;

    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;

    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    const schemeMetaDataId =
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;

    if (caseId && visitId && schemeMetaDataId) {
      try {
        const AllDocumentList = await updateAllDocumentsData(
          caseId,
          visitId,
          payloadSchema,
        );
        message.success(`Response documents saved successfully`);
        fetchDocuments();
      } catch (error) {
        message.error(`Something went wrong`);
      }
    }
  };

  const handleUpload = async () => {
    if (selectedFiles?.length > 0) {
      const CollectPostData = new FormData();
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        CollectPostData.append(
          "hospitalId",
          patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        CollectPostData.append(
          "patientId",
          patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.casesResponse?.id) {
        CollectPostData.append(
          "caseId",
          patients?.fetchBasicDetails?.casesResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        CollectPostData.append(
          "visitId",
          patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
        );
      }
      CollectPostData.append("documentType", previewFiles?.[0]?.documentType);

      selectedFiles?.map((fileObj, index) =>
        CollectPostData.append(`file${index}`, fileObj.file),
      );

      // if (selectedFiles?.length > 0) {
      //   selectedFiles.forEach((fileObj, index) => {
      //     // Extract the actual File object from your structure
      //     const actualFile = fileObj.file; // This is the originFileObj (actual File)

      //     if (actualFile instanceof File) {
      //       CollectPostData.append("files", actualFile);
      //     }
      //   });
      // }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: auth.accessToken || "",
          }),
        ).unwrap();

        if (
          documentRes?.[0]?.aadharNumber &&
          documentRes?.[0]?.documentLabelType === "Aadhar card"
        ) {
          form.setFieldsValue({
            patientDetails: {
              aadharNumber: documentRes?.[0]?.aadharNumber,
            },
          });
        }

        // const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);
        const updatedDocuments = [...documentUrls, ...documentRes];

        setDocumentUrls(updatedDocuments);
        setPreviewModalVisible(false);
        message.success(
          `${documentRes?.[0]?.documentLabelType} uploaded successfully`,
        );
        await handleAttachDocuments(documentRes);
        setSelectedFiles([]);
        setPreviewFiles([]);
        setUploadFileList([]);
      } catch (error) {
        console.error("Failed to upload document:", error);
      }
    } else {
      const CollectPostData = new FormData();
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        CollectPostData.append(
          "hospitalId",
          patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        CollectPostData.append(
          "patientId",
          patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.casesResponse?.id) {
        CollectPostData.append(
          "caseId",
          patients?.fetchBasicDetails?.casesResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        CollectPostData.append(
          "visitId",
          patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
        );
      }
      CollectPostData.append("documentType", previewFile.documentType);

      if (selectedFile?.file) {
        CollectPostData.append("file", selectedFile?.file);
      }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: auth.accessToken || "",
          }),
        ).unwrap();

        if (
          documentRes?.[0]?.aadharNumber &&
          documentRes?.[0]?.documentLabelType === "Aadhar card"
        ) {
          form.setFieldsValue({
            patientDetails: {
              aadharNumber: documentRes?.[0]?.aadharNumber,
            },
          });
        }

        // const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);
        const updatedDocuments = [...documentUrls, ...documentRes];

        setDocumentUrls(updatedDocuments);
        setPreviewModalVisible(false);
        message.success(
          `${documentRes?.[0]?.documentLabelType} uploaded successfully`,
        );
        await handleAttachDocuments(documentRes);
        setSelectedFile(null);
        setPreviewFile({
          url: "",
          name: "",
          showUpload: false,
          documentType: "",
        });
      } catch (error) {
        console.error("Failed to upload document:", error);
      }
    }
  };

  // const handleUpload = async () => {
  //   const CollectPostData = new FormData();
  //   if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
  //     CollectPostData.append(
  //       "hospitalId",
  //       patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.patientResponse?.id) {
  //     CollectPostData.append(
  //       "patientId",
  //       patients?.fetchBasicDetails?.patientResponse?.id?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.casesResponse?.id) {
  //     CollectPostData.append(
  //       "caseId",
  //       patients?.fetchBasicDetails?.casesResponse?.id?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
  //     CollectPostData.append(
  //       "visitId",
  //       patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
  //     );
  //   }
  //   CollectPostData.append("documentType", previewFile.documentType);

  //   if (selectedFile?.file) {
  //     CollectPostData.append("file", selectedFile?.file);
  //   }

  //   try {
  //     const documentRes = await dispatch(
  //       UploadDocument({
  //         formData: CollectPostData,
  //         accessToken: auth.accessToken || "",
  //       })
  //     ).unwrap();

  //     const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);

  //     setDocumentUrls(updatedDocuments);
  //     setPreviewModalVisible(false);
  //     message.success(
  //       `${documentRes?.documentLabelType} uploaded successfully`
  //     );
  //   } catch (error) {
  //     console.error("Failed to upload document:", error);
  //   }
  // };

  useEffect(() => {
    const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
    const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
    const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
    const casePreAuthResponse =
      referralResponse?.casePreAuthResponse ||
      emergencyResponse?.casePreAuthResponse;
    const claimId = referralResponse?.claimId || emergencyResponse?.claimId;

    let intervalId: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    let pollCount = 0;
    const maxPolls = 24;
    const modifiedAt = preAuth?.portalData?.modifiedAt;

    const modifiedTime = new Date(modifiedAt).getTime();
    const now = Date.now();
    const diffSeconds = (now - modifiedTime) / 1000;

    if (casePreAuthResponse?.initiated && !claimId && diffSeconds <= 78) {
      intervalId = setInterval(() => {
        pollCount++;
        if (
          patients?.fetchBasicDetails?.casesResponse?.id &&
          patients?.fetchBasicDetails?.visitInfoResponse?.id
        ) {
          dispatch(
            getPortalDataEchs({
              caseId: patients?.fetchBasicDetails?.casesResponse?.id,
              visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
            }),
          );
        }

        if (claimId) {
          if (intervalId) clearInterval(intervalId);
          if (timeoutId) clearTimeout(timeoutId);
        }

        if (pollCount >= maxPolls) {
          if (intervalId) clearInterval(intervalId);
          // setShowManualClaimIdModal(true);
        }
      }, 5000);

      timeoutId = setTimeout(() => {
        if (intervalId) clearInterval(intervalId);
        // setShowManualClaimIdModal(true);
      }, 120000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
    // Add dependencies as needed
  }, [
    dispatch,
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    preAuth?.portalData?.casePreAuthEchs,
    preAuth?.portalData?.modifiedAt,
  ]);

  const actionButtonStyle = React.useMemo(
    () => ({
      backgroundColor: "rgb(0, 76, 143)",
      color: "#fff",
      boxShadow: "0px 2px 8px 0px #00000040",
      width: "100%",
    }),
    [],
  );

  const actionButtonContainerStyle = React.useMemo(
    () => ({ width: "16%" }),
    [],
  );

  const renderActionButtons = () => {
    const casePreAuth = preAuth?.portalData?.casePreAuthEchs;
    const referralResponse = casePreAuth?.casePreAuthEchsReferralResponse;
    const emergencyResponse = casePreAuth?.casePreAuthEchsEmergencyResponse;
    const casePreAuthResponse =
      referralResponse?.casePreAuthResponse ||
      emergencyResponse?.casePreAuthResponse;

    // If there's a claim ID in either response, don't show any buttons
    // if (referralResponse?.claimId || emergencyResponse?.claimId) {
    //   return null;
    // }

    const buttonStyle = actionButtonStyle;
    const buttonContainerStyle = actionButtonContainerStyle;

    const showUpdateButton =
      (referralResponse?.id || emergencyResponse?.id) &&
      !casePreAuthResponse?.initiated &&
      !casePreAuthResponse?.submitted &&
      !(auth?.role?.role === "FO_STAFF" && casePreAuthResponse?.intimated);

    const showIntimateButton = true;
    //   && !casePreAuthResponse?.intimated &&
    //   !casePreAuthResponse?.initiated &&
    //   !casePreAuthResponse?.approved &&
    //   !casePreAuthResponse?.submitted;

    const showQCApproveButton =
      hasFunctionality("preAuthApprover") &&
      !casePreAuthResponse?.initiated &&
      !casePreAuthResponse?.approved &&
      !casePreAuthResponse?.submitted;

    const showInitiateButton =
      hasFunctionality("casePreAuthSubmission") &&
      !casePreAuthResponse?.initiated &&
      !casePreAuthResponse?.submitted;

    return (
      <div
        style={{
          marginTop: 20,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "30px",
          borderTop: "1px solid #D9D9D9",
          padding: "15px 20px",
        }}
      >
        {/* {!(referralResponse?.id || emergencyResponse?.id) ? (
          <div style={buttonContainerStyle}>
            <Button
              type="primary"
              style={buttonStyle}
              onClick={() => handleSaveConfirmationModal("save")}
            >
              {"SAVE"}
            </Button>
          </div>
        ) : (
          <></>
        )} */}

        {/* {showUpdateButton ? (
          <div style={buttonContainerStyle}>
            <Button
              type="primary"
              style={buttonStyle}
              onClick={() => handleSaveConfirmationModal("update")}
            >
              {"UPDATE"}
            </Button>
          </div>
        ) : (
          <></>
        )} */}
        {/* Save/Update Button */}

        {/* Intimate Button */}
        {showIntimateButton && (
          <div style={buttonContainerStyle}>
            <Button
              style={buttonStyle}
              className="me-2"
              // onClick={() => handleSaveConfirmationModal("intimate")}
              onClick={handleSentToQC}
            >
              {"Generate Claim Id"}
            </Button>
          </div>
        )}

        {/* QC Approve Button */}
        {/* {showQCApproveButton && (
          <div style={buttonContainerStyle}>
            <Button
              loading={isApprovedInitiated}
              className="me-2"
              onClick={() => {
                if (!validateProposedTreatmentYes()) return;
                handleSaveConfirmationModal("approve");
              }}
              disabled={isApproved}
              style={{
                ...buttonStyle,
                opacity: !isApproved ? 1 : 0.5,
                pointerEvents: !isApproved ? "auto" : "none",
              }}
            >
              QC Approve
            </Button>
          </div>
        )} */}

        {/* Submit to Portal Button */}
        {/* {showInitiateButton && (
          <div style={buttonContainerStyle}>
            <Button
              type="primary"
              style={buttonStyle}
              // onClick={() => handleSaveConfirmationModal("submit")}
              onClick={() => {
                if (!validateProposedTreatmentYes()) return;
                handleSubmit();
              }}
            >
              {patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal
                ? "Initiate"
                : "Submit Manually"}
            </Button>
          </div>
        )} */}
      </div>
    );
  };

  const validateProposedTreatmentYes = () => {
    const visitType = patients?.fetchBasicDetails?.visitInfoResponse?.visitType;

    if (!(visitType === "IP" || visitType === "EMERGENCY")) return true;

    const proposed: Record<string, any> =
      form.getFieldValue("patientProposedTreatmentData") || {};

    const hasYes = Object.entries(proposed)
      ?.filter(([key]) => !String(key).endsWith("Value"))
      ?.some(
        ([, val]) =>
          typeof val === "string" && val?.toLowerCase().includes(":y"),
      );

    if (!hasYes) {
      errorMessageModal(
        "Atleast single Patient Proposed Treatment is mandatory.",
      );
      return false;
    }
    return true;
  };

  const handleUpdateRegistrationValidation = async (data: any) => {
    try {
      const res = await updateRegistrationValidationDetails(
        patients?.fetchBasicDetails?.casesResponse?.id,
        patients?.fetchBasicDetails?.visitInfoResponse?.id,
        data,
      );
      successMessage("Details Updated Successfully");
      // setIsRegistraionValidatedModalOpen(false);
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        dispatch(
          getBasicDetails({
            id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          }),
        );
      }
      dispatch(
        getPortalDataEchs({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id || 0,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id || 0,
        }),
      );
    } catch (err) {
      errorMessage("Please, Try again later");
    }
  };

  return (
    <>
      <ReferralAcceptance
        form={form}
        visitType={patients?.fetchBasicDetails?.visitInfoResponse?.visitType}
        loading={loading}
        documentTypes={documentTypes}
        documentUrls={documentUrls}
        documentRequestResponseDocuments={documentRequestResponseDocuments}
        handleFileChange={handleFileChange}
        handleOpen={handleOpen}
        handleSaveData={handleSaveData}
        isSubmitting={isSubmitting}
        preAuth={preAuth}
        hasFunctionality={hasFunctionality}
        handleSaveConfirmationModal={handleSaveConfirmationModal}
        isApproved={isApproved}
        isApprovedInitiated={isApprovedInitiated}
        activeKey={activeKey}
        referralPresent={referralPresent}
        ageAboveSeventyWithoutReferral={ageAboveSeventyWithoutReferral}
        ageBelowSeventyWithoutReferral={ageBelowSeventyWithoutReferral}
        renderActionButtons={renderActionButtons}
        portalSubmission={portalSubmission}
        uploadFileList={uploadFileList}
        isIntimated={
          preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsReferralResponse
            ?.casePreAuthResponse?.intimated ||
          preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsEmergencyResponse
            ?.casePreAuthResponse?.intimated
        }
        removedDiagnoses={removedDiagnoses}
        setRemovedDiagnoses={setRemovedDiagnoses}
        polyclinicList={polyclinicList}
        showVerifyButton={showVerifyButton}
        registrationValidationStatus={registrationValidationStatus}
        isValidationLoading={isValidationLoading}
        handleRegistrationValidation={handleRegistrationValidation}
      />

      {selectedFile?.file && !selectedFiles?.length && (
        <FilePreviewModal
          isVisible={previewModalVisible}
          onClose={() => setPreviewModalVisible(false)}
          fileUrl={previewFile.url}
          fileName={previewFile.name}
          documentType={previewFile.documentType}
          handleUpload={handleUpload}
          showUploadButton={previewFile.showUpload}
          key={selectedFile?.file?.lastModified || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      <PatientDetailsStageDocument
        // key={documentUrl}
        handleCancel={handleCancelPreview}
        handleDeleteDocument={handleDeleteList}
        open={previewDocument}
        handleDownloadDocument={handleDownloadDocument}
        // handleSetDocumentUrl={handleSetDocumentUrl}
        documents={documentUrls}
        handleDeficiencyModal={handleDeficiencyModalOpen}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        setDocumentName={setDocumentName}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setDocumentUrls}
        activeKey={activeKey}
        clickedActiveTab={clickedActiveTab}
        isSubmittedStatus={portalSubmission}
        isIntimated={preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsReferralResponse?.casePreAuthResponse?.intimated || preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsEmergencyResponse?.casePreAuthResponse?.intimated}
        fetchingDeficiencyData={fetchingDeficiencyData}
        clickedActiveDocumentUrl={clickedActiveDocumentUrl}
      />
      <DocumentDeficiencyModal
        key={previewDocumentUrl}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        documentUrl={previewDocumentUrl}
        documentName={documentName}
        handleDownloadDocument={handleDownloadDocument}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setDocumentUrls}
      />

      <ConfirmSubmissionModalStage
        open={confirmationModal}
        title={title}
        handleCancel={() => setConfirmationModal(false)}
        handleSave={handleSave}
        isLoading={
          isSaving || isIntimating || isApprovedInitiated || isSubmitting
        }
      />

      <ManuallyClaimIdPortal
        open={manuallyClaimIdPortal}
        title={"Enter Claim Id"}
        handleCancel={() => setManuallyClaimIdPortal(false)}
        handleSave={handleSaveManually}
        footer={"Save"}
        isLoading={isSubmitting}
      />

      <PortalSubmissionModal
        open={isSubmittedOnPortal}
        title={"Is PreAuth Already Submitted on Portal?"}
        handleCancel={handleCancelSubmissionPortal}
        handleSave={handleManuallyIdEnter}
      />

      <ManuallyIdModal
        open={isManuallyEnteredId}
        title={"Enter Claim Id"}
        handleCancel={handleCancelManuallyId}
        handleSave={handleSaveManually}
        footer={"Save"}
        isLoading={isLoading}
      />

      <PortalSubmitModal
        open={submissionModal}
        title={"Do You want to Register PreAuth?"}
        handleOk={handleOk}
        handleCancel={handleRegistrationCancel}
        handleSave={handleSaveModalOption}
        isLoading={isSubmitting}
      />

      <ManuallyIdModalForQC
        open={isManuallyEnteredIdForQc}
        title={"Enter Claim Id"}
        handleCancel={() => setIsManuallyEnteredIdForQc(false)}
        footer={"Save"}
        isLoading={isLoadingForQc}
        handleSave={handleSaveManuallyForQc}
      />

      {/* Intimate */}
      <PortalSubmissionModal
        open={isClaimIdPresent}
        title={"Do You have Claim Id?"}
        handleCancel={handleCancelSubmissionForQc}
        handleSave={handleManuallyIdEnterForQc}
      />

      {/* Generation of claim Id */}
      <GenerateClaimIdModal
        open={isGenerateClaimId}
        title={"Do You want to Generate Claim Id?"}
        handleCancel={handleCancelGenerateClaimId}
        handleSave={handleSaveGenerateClaimId}
        isLoading={isGenerateClaimIdLoading}
      />

      {/* BO send to portal on yes */}
      <PortalSubmissionModalForQc
        open={isSubmitOnPortal}
        title={"Do You want to submit data on portal?"}
        // handleCancel={() => {
        //   handleSaveManuallyNotOnPortal();
        //   setIsSubmitOnPortal(false);
        // }}
        handleCancel={handleSaveManuallyNotOnPortal}
        handleSave={handleSaveManuallyOnPortal}
        isLoading={isSubmitting}
        loading={isSubmittingNotOnPortal}
      />

      <ManuallyIdModal
        open={isManuallyEnteredId}
        title={"Enter Claim Id"}
        handleCancel={handleCancelManuallyId}
        handleSave={handleSaveManually}
        footer={"Save"}
        isLoading={isLoading}
      />

      <PortalSubmitModal
        open={sentForQcModal}
        title={"Do you want to send PreAuth for QC?"}
        handleOk={() => setSentForQcModal(false)}
        handleCancel={handleRegistrationCancelForQc}
        handleSave={handleIntimate}
        isLoading={isIntimating} 
      />

      {selectedFiles?.length > 0 && (
        <MultipleFilesPreviewModal
          isVisible={previewModalVisible}
          onClose={() => {
            setPreviewModalVisible(false);
            setSelectedFiles([]);
            setUploadFileList([]);
          }}
          files={previewFiles}
          handleUpload={handleUpload}
          showUploadButton={previewFiles?.some((f) => f.showUpload)}
          key={previewFiles?.map((f) => f.name).join(",") || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      <ClaimIdValidationModal
        open={isRegistraionValidatedModalOpen}
        onCancel={() => setIsRegistraionValidatedModalOpen(false)}
        validationRes={validationRes}
        onUpdate={handleUpdateRegistrationValidation}
        totalCases={cases?.caseType || []}
      />
    </>
  );
};

export default ReferralAcceptanceContainer;
